import { WSClient } from "./ws-client";
import { Session, ToolWithHandler } from "./session";
import { buildSessionTranscript } from "./session-transcript";
import { resolveApiUrl, resolveAuthTokenForApiUrl } from "./endpoints";
import { invokeRegisteredEffect } from "./effect-runtime";
import {
  deriveRuntimeBaseUrl,
  normalizeHeapSnapshot,
  rankRecordSearchResult,
  toRecordSearchResult,
} from "./client-normalizers";
import {
  isLocalControlUrl,
  isRetryableEffectRegistrationError,
  isRetryableLocalWorkerRestart,
  isRetryableRecordObjectsError,
  isRetryableSessionDataError,
  sleep,
  withTimeout,
} from "./client-transport";
import {
  recordOpenAIUsageSpend as postOpenAIUsageSpend,
  type GranularSpendContext,
  type OpenAIUsageSpendEvent,
  type RecordOpenAIUsageSpendResult,
} from "./spend";
import {
  buildEffectMetamodelMutations,
  buildFieldMetamodelMutations,
  buildModelMetamodelMutations,
} from "./manifest-metamodels";
import type {
  EffectMetamodelManifestSpec,
  FieldMetamodelManifestSpec,
} from "@granular-software/metamodel-core";
import type {
  AccessTokenProvider,
  GranularOptions,
  ConnectOptions,
  OpenEnvironmentOptions,
  CreateSessionOptions,
  SessionHeapSnapshot,
  RecordUserOptions,
  User,
  Subject,
  Sandbox,
  SandboxListResponse,
  CreateSandboxData,
  PermissionProfile,
  PermissionProfileFile,
  PermissionProfileListResponse,
  CreatePermissionProfileData,
  EnvironmentData,
  EnvironmentListResponse,
  CreateEnvironmentData,
  Assignment,
  AssignmentListResponse,
  DeleteResponse,
  GraphQLResult,
  RelationshipInfo,
  ModelRef,
  DefineRelationshipOptions,
  ManifestContent,
  ManifestOperation,
  ManifestPropertySpec,
  ManifestImport,
  ManifestEffectDeclaration,
  ManifestEventStreamDef,
  StreamEvent,
  StreamSubscription,
  StreamStats,
  RecordObjectOptions,
  RecordObjectResult,
  RecordObjectsOptions,
  RecordObjectsChunkInfo,
  RecordImportOptions,
  RecordImport,
  EnvironmentRecordImportSummary,
  EnvironmentSetupSummary,
  EnvironmentSetupTriggerReason,
  EnvironmentImporter,
  RunEnvironmentImporterOptions,
  RecordImportStatus,
  RecordMentionInput,
  EnvironmentFeedbackRecord,
  RecordSearchOptions,
  RecordSearchResult,
  ToolSchema,
  ConversationSessionInfo,
  SessionCollectionListOptions,
  SessionCollectionListResult,
  SessionConversationMessage,
  SessionDocumentResult,
  SessionFileRecord,
  SessionFileUploadOptions,
  SessionHeapEntry,
  SessionHeapList,
  SessionHeapVariable,
  SessionJobListOptions,
  SessionJobRecord,
  SessionTimelineEvent,
  SessionTranscriptEntry,
  UserEnvironmentState,
  UserEnvironmentStateOptions,
  MarkUserEnvironmentReadOptions,
  WSDisconnectInfo,
  WSReconnectErrorInfo,
  GranularQuotaProgress,
} from "./types";

// ── Built-in modules ──

/**
 * Standard modules: base types available via `imports: [{ alias: "@std", name: "standard_modules" }]`
 */
const STANDARD_MODULES_OPERATIONS: ManifestOperation[] = [
  {
    create: "entity",
    has: { id: { value: "auto-generated" }, createdAt: { value: undefined } },
  },
  { create: "class", extends: "entity", has: {} },
  {
    create: "user",
    extends: "entity",
    has: {
      email: { value: undefined },
      firstName: { value: undefined },
      lastName: { value: undefined },
    },
  },
  {
    create: "company",
    extends: "entity",
    has: { name: { value: undefined }, website: { value: undefined } },
  },
  { create: "string", has: {} },
  { create: "number", has: {} },
  { create: "boolean", has: {} },
  {
    create: "tool_parameter",
    has: {
      name: { value: undefined },
      type: { value: "string" },
      description: { value: undefined },
      required: { value: false },
    },
  },
];

/**
 * Registry of built-in modules that can be imported in manifests
 */
const BUILTIN_MODULES: Record<string, ManifestOperation[]> = {
  standard_modules: STANDARD_MODULES_OPERATIONS,
};

/**
 * Keep individual direct-write HTTP requests bounded so large synchronous
 * `recordObjects()` calls do not hit gateway/runtime request timeouts.
 */
const DEFAULT_DIRECT_RECORD_OBJECTS_REQUEST_BATCH_SIZE = 100;
const MAX_RECORD_OBJECTS_CONCURRENCY = 16;
const DEFAULT_DIRECT_RECORD_OBJECTS_RETRY_COUNT = 3;
const DEFAULT_DIRECT_RECORD_OBJECTS_RETRY_DELAY_MS = 1_000;
const LOCAL_CONTROL_REQUEST_RETRY_COUNT = 4;
const LOCAL_CONTROL_REQUEST_RETRY_DELAY_MS = 500;
const SESSION_DATA_REQUEST_RETRY_COUNT = 4;
const SESSION_DATA_REQUEST_RETRY_DELAY_MS = 500;
const EFFECT_HOST_CONNECT_TIMEOUT_MS = 15_000;
const SESSION_CONNECT_TIMEOUT_MS = 15_000;

type SessionFileUploadBody = Blob | ArrayBuffer | Uint8Array | string;
type SessionDataBody = any;
type SessionDataHeaders = any;

function filenameFromUploadBody(body: SessionFileUploadBody): string | null {
  const maybe = body as { name?: unknown };
  return typeof maybe.name === "string" && maybe.name.trim()
    ? maybe.name.trim()
    : null;
}

function contentTypeFromUploadBody(body: SessionFileUploadBody): string | null {
  const maybe = body as { type?: unknown };
  return typeof maybe.type === "string" && maybe.type.trim()
    ? maybe.type.trim()
    : null;
}

function bodyInitFromSessionFileUpload(
  body: SessionFileUploadBody,
): SessionDataBody {
  if (typeof body === "string") return body;
  if (body instanceof ArrayBuffer) return body;
  if (ArrayBuffer.isView(body)) {
    return body.buffer.slice(
      body.byteOffset,
      body.byteOffset + body.byteLength,
    );
  }
  return body;
}
const EFFECT_CATALOG_SYNC_TIMEOUT_MS = 120_000;
const EFFECT_CATALOG_SYNC_RETRY_COUNT = 3;
const EFFECT_CATALOG_SYNC_RETRY_DELAY_MS = 1_000;

type RecordObjectsChunkPlan = {
  chunkIndex: number;
  chunkCount: number;
  offset: number;
  slice: RecordObjectOptions[];
};

function planRecordObjectsChunks(
  records: RecordObjectOptions[],
  batchSize: number,
): RecordObjectsChunkPlan[] {
  const total = records.length;
  const size = Math.max(1, Math.min(batchSize, total));
  const chunkCount = Math.ceil(total / size);
  const plans: RecordObjectsChunkPlan[] = [];
  for (
    let offset = 0, chunkIndex = 0;
    offset < total;
    offset += size, chunkIndex += 1
  ) {
    const slice = records.slice(offset, offset + size);
    plans.push({ chunkIndex, chunkCount, offset, slice });
  }
  return plans;
}

function computeEffectKey(effect: {
  name: string;
  className?: string | null;
  static?: boolean;
}): string {
  const attachedClass = effect.className?.trim();
  if (!attachedClass) {
    return `global:${effect.name}`;
  }
  return effect.static
    ? `class:${attachedClass}:static:${effect.name}`
    : `class:${attachedClass}:instance:${effect.name}`;
}

function computeEffectVersionSelectorKey(
  selector?: ToolSchema["versionSelector"],
): string {
  if (!selector || selector.mode === "all") {
    return "all";
  }
  return `${selector.mode}:${selector.versionNumber}`;
}

function computeEffectRegistrationKey(effect: ToolSchema): string {
  return `${computeEffectKey(effect)}@${computeEffectVersionSelectorKey(
    effect.versionSelector,
  )}`;
}

function buildEffectHostUrl(
  apiUrl: string,
  sandboxId: string,
  effectClientId: string,
  clientId: string,
  effectHostUrl?: string,
): string {
  const overrideUrl =
    effectHostUrl ||
    process.env.GRANULAR_EFFECT_HOST_URL ||
    process.env.EFFECT_HOST_URL;
  const api = new URL(apiUrl);
  const localRuntimeBase = process.env.RUNTIME_ORCHESTRATOR_URL || "";
  const url = new URL(overrideUrl || localRuntimeBase || apiUrl);

  if (url.protocol === "https:") {
    url.protocol = "wss:";
  } else if (url.protocol === "http:") {
    url.protocol = "ws:";
  }

  if (
    !overrideUrl &&
    isLocalControlUrl(apiUrl) &&
    !localRuntimeBase &&
    api.pathname.endsWith("/granular")
  ) {
    url.pathname = "/granular/effects/connect";
  } else if (url.pathname.endsWith("/granular/ws/connect")) {
    url.pathname = url.pathname.replace(/\/ws\/connect$/, "/effects/connect");
  } else if (url.pathname.endsWith("/granular")) {
    url.pathname =
      localRuntimeBase && isLocalControlUrl(url.toString())
        ? "/granular/orchestrator/effects/connect"
        : `${url.pathname.replace(/\/$/, "")}/effects/connect`;
  } else if (url.pathname.endsWith("/v2/ws/connect")) {
    url.pathname = url.pathname.replace(/\/ws\/connect$/, "/effects/connect");
  } else if (url.pathname.endsWith("/v2/ws")) {
    url.pathname = url.pathname.replace(/\/ws$/, "/effects/connect");
  } else if (
    url.pathname === "/" &&
    isLocalControlUrl(url.toString()) &&
    (url.port === "8791" || (!overrideUrl && Boolean(localRuntimeBase)))
  ) {
    url.pathname = "/granular/orchestrator/effects/connect";
  } else if (url.pathname.endsWith("/ws/connect")) {
    url.pathname = url.pathname.replace(/\/ws\/connect$/, "/effects/connect");
  } else if (url.pathname.endsWith("/ws")) {
    url.pathname = url.pathname.replace(/\/ws$/, "/effects/connect");
  } else {
    url.pathname = "/granular/effects/connect";
  }

  url.search = "";
  url.hash = "";
  url.searchParams.set("sandboxId", sandboxId);
  url.searchParams.set("effectClientId", effectClientId);
  url.searchParams.set("clientId", clientId);
  return url.toString();
}

function normalizeSubject(subject: Subject): Subject {
  const granularId = subject.granularId || subject.subjectId;
  const userId = subject.userId || subject.identityId || granularId;
  return {
    ...subject,
    granularId,
    userId,
    subjectId: granularId,
    identityId: subject.identityId || userId,
  };
}

function normalizeUser(user: User): User {
  const granularId = user.granularId || user.subjectId;
  const userId = user.userId || user.identityId || granularId;
  return {
    ...user,
    granularId,
    userId,
    subjectId: granularId,
    identityId: user.identityId || userId,
    permissions: Array.isArray(user.permissions) ? user.permissions : [],
  };
}

function normalizeEnvironmentSetupSummary(
  setup?: EnvironmentSetupSummary | null,
): EnvironmentSetupSummary | null {
  if (!setup) {
    return null;
  }

  const queuedRecords = Number(setup.queuedRecords || 0);
  const processingRecords = Number(setup.processingRecords || 0);

  return {
    ...setup,
    setupRunId: String((setup as { setupRunId?: string }).setupRunId || ""),
    environmentId: String(setup.environmentId || ""),
    sandboxId: String(setup.sandboxId || ""),
    subjectId: String(setup.subjectId || ""),
    triggerReason:
      setup.triggerReason === "fresh_after_version_update"
        ? "fresh_after_version_update"
        : "new_environment",
    lifecycleStatus:
      setup.lifecycleStatus === "completed" ||
      setup.lifecycleStatus === "failed"
        ? setup.lifecycleStatus
        : "running",
    stage: typeof setup.stage === "string" ? setup.stage : null,
    totalObjectsToImport: Number(setup.totalObjectsToImport || 0),
    totalImports: Number(setup.totalImports || 0),
    activeImports: Number(setup.activeImports || 0),
    totalRecords: Number(setup.totalRecords || 0),
    queuedRecords,
    processingRecords,
    completedRecords: Number(setup.completedRecords || 0),
    failedRecords: Number(setup.failedRecords || 0),
    canceledRecords: Number(setup.canceledRecords || 0),
    awaitingRecords:
      Number(setup.awaitingRecords || 0) || queuedRecords + processingRecords,
    errorMessage:
      typeof setup.errorMessage === "string" ? setup.errorMessage : null,
    startedAt: Number(setup.startedAt || Date.now()),
    hookCompletedAt:
      setup.hookCompletedAt == null ? null : Number(setup.hookCompletedAt),
    finishedAt: setup.finishedAt == null ? null : Number(setup.finishedAt),
    updatedAt: Number(setup.updatedAt || Date.now()),
  };
}

function normalizeEnvironmentData(
  environment: EnvironmentData,
): EnvironmentData {
  const buildPolicy =
    environment.buildPolicy ||
    environment.tracking ||
    (environment.tagId
      ? { mode: "tag" as const, tagId: environment.tagId }
      : {
          mode: "pinned" as const,
          versionId: environment.versionId || environment.buildId,
        });

  const environmentName =
    environment.environment || environment.envName || "prod";
  return {
    ...environment,
    ontologyId: environment.ontologyId || environment.sandboxId,
    versionId: environment.versionId || environment.buildId,
    envName: environmentName,
    environment: environmentName,
    buildPolicy,
    tracking: environment.tracking || buildPolicy,
    setup: normalizeEnvironmentSetupSummary(environment.setup),
  };
}

interface ResolvedConnectUser {
  granularId: string;
  userId: string;
  name?: string;
  email?: string;
  permissions: string[];
}

interface SandboxEffectHost {
  sandboxId: string;
  effectClientId: string;
  clientId: string;
  wsClient: WSClient;
  heartbeatTimer: ReturnType<typeof setInterval> | null;
  heartbeatInFlight: boolean;
  recovering: boolean;
  syncPromise: Promise<void> | null;
}

type EnvironmentImporterHandler = (
  importer: EnvironmentImporter,
) => Promise<void> | void;

interface ResolvedEnvironmentImporter {
  handler: EnvironmentImporterHandler;
  sourceOntology: string;
}

interface ResolvedOpenEnvironmentData {
  environment: EnvironmentData;
  requestedOntology: string;
  sandboxId: string;
  subjectId: string;
  setupTriggerReason?: EnvironmentSetupTriggerReason;
}

/**
 * Environment is the sessionless handle for one resolved ontology environment.
 *
 * Use it to query or mutate environment data directly, or to open live runtime
 * sessions through `environment.sessions.*` when you need jobs, prompts, or a
 * synced Automerge document.
 */
export class Environment {
  private granular: Granular;
  private envData: EnvironmentData;
  private _apiKey: string;
  private _apiEndpoint: string;

  constructor(
    granular: Granular,
    envData: EnvironmentData,
    apiKey: string,
    apiEndpoint: string,
  ) {
    this.granular = granular;
    this.envData = envData;
    this._apiKey = apiKey;
    this._apiEndpoint = apiEndpoint;
  }

  /** The environment ID */
  get environmentId(): string {
    return this.envData.environmentId;
  }

  /** The sandbox ID */
  get sandboxId(): string {
    return this.envData.sandboxId;
  }

  /** The ontology ID */
  get ontologyId(): string {
    return this.envData.ontologyId || this.envData.sandboxId;
  }

  /** The subject ID */
  get subjectId(): string {
    return this.envData.subjectId;
  }

  /** The named environment slot, such as dev or prod */
  get envName(): string {
    return this.envData.envName;
  }

  /** The named environment slot, such as dev or prod */
  get environment(): string {
    return this.envData.environment || this.envData.envName;
  }

  /** The resolved ontology version backing this environment */
  get versionId(): string {
    return this.envData.versionId || this.envData.buildId;
  }

  /** Internal Granular user identifier for this environment */
  get granularId(): string {
    return this.envData.subjectId;
  }

  /** The permission profile ID */
  get permissionProfileId(): string {
    return this.envData.permissionProfileId;
  }

  /** The current build policy backing this environment */
  get buildPolicy() {
    return this.envData.buildPolicy;
  }

  /** The current update state relative to the followed tag */
  get updateState(): EnvironmentData["updateState"] {
    return this.envData.updateState;
  }

  /** The latest setup/import run summary for this environment, when available. */
  get setup(): EnvironmentSetupSummary | null {
    return this.envData.setup || null;
  }

  /** Convenience flag for whether this environment trails the current tag target */
  get isOutdated(): boolean {
    return this.envData.updateState === "update_available";
  }

  /** The followed tag name when this environment is tag-tracked */
  get tag(): string | null {
    return this.envData.tag?.name || this.envData.buildPolicy.tagName || null;
  }

  /** The GraphQL API endpoint URL */
  get apiEndpoint(): string {
    return this._apiEndpoint;
  }

  /** Internal auth token used for control-plane and runtime fallback requests */
  get authToken(): string {
    return this._apiKey;
  }

  /** Base runtime URL derived from the GraphQL endpoint */
  get runtimeBaseUrl(): string {
    return this.getRuntimeBaseUrl();
  }

  syncEnvironmentData(envData: EnvironmentData): void {
    this.envData = normalizeEnvironmentData(envData);
  }

  get sessions() {
    return {
      list: async (options?: {
        status?: "active" | "closed" | "all";
      }): Promise<ConversationSessionInfo[]> =>
        this.listSessions(options?.status || "active"),
      create: async (
        options?: CreateSessionOptions,
      ): Promise<EnvironmentSession> => this.createSession(options),
      connect: async (
        sessionId: string,
        options?: { clientId?: string },
      ): Promise<EnvironmentSession> => this.connectSession(sessionId, options),
      reopen: async (
        sessionId: string,
        options?: { clientId?: string },
      ): Promise<EnvironmentSession> => this.reopenSession(sessionId, options),
      close: async (
        sessionId: string,
        session?: EnvironmentSession | null,
      ): Promise<void> => this.closeSession(sessionId, session),
      state: async (
        options?: UserEnvironmentStateOptions,
      ): Promise<UserEnvironmentState> => this.getUserEnvironmentState(options),
      markRead: async (
        options: MarkUserEnvironmentReadOptions,
      ): Promise<UserEnvironmentState["readAtBySessionId"]> =>
        this.markUserEnvironmentSessionsRead(options),
    };
  }

  get userEnvironmentState() {
    return {
      get: async (
        options?: UserEnvironmentStateOptions,
      ): Promise<UserEnvironmentState> => this.getUserEnvironmentState(options),
      markRead: async (
        options: MarkUserEnvironmentReadOptions,
      ): Promise<UserEnvironmentState["readAtBySessionId"]> =>
        this.markUserEnvironmentSessionsRead(options),
    };
  }

  get data() {
    return {
      record: async (
        record: RecordObjectOptions,
      ): Promise<RecordObjectResult> => this.recordObject(record),
      recordMany: async (
        records: RecordObjectOptions[],
        options?: RecordObjectsOptions,
      ): Promise<RecordObjectResult[]> => this.recordObjects(records, options),
      import: async (
        records: RecordObjectOptions[],
        options?: { batchSize?: number },
      ): Promise<RecordImport> => this.enqueueRecordImport(records, options),
      listImports: async (
        status?: RecordImportStatus,
      ): Promise<RecordImport[]> => this.listRecordImports(status),
      getImport: async (importId: string): Promise<RecordImport> =>
        this.getRecordImport(importId),
      getImportSummary: async (): Promise<EnvironmentRecordImportSummary> =>
        this.getRecordImportSummary(),
      cancelImport: async (importId: string): Promise<RecordImport> =>
        this.cancelRecordImport(importId),
      getAwaitingCount: async (): Promise<number> =>
        this.getAwaitingRecordCount(),
    };
  }

  get feedback() {
    return {
      list: async (): Promise<EnvironmentFeedbackRecord[]> =>
        this.listFeedback(),
    };
  }

  /**
   * Sessionless environments do not own a live transport, so disconnecting the
   * environment handle itself is a no-op. This keeps the public surface
   * symmetric with `EnvironmentSession.disconnect()` and lets callers always
   * clean up safely without tracking whether they currently hold an environment
   * or a session.
   */
  async disconnect(): Promise<void> {}

  async listSessions(
    status: "active" | "closed" | "all" = "active",
  ): Promise<ConversationSessionInfo[]> {
    if (status === "all") {
      const [active, closed] = await Promise.all([
        this.granular.listOpenSessions({ environmentId: this.environmentId }),
        this.granular.listClosedSessions({ environmentId: this.environmentId }),
      ]);
      return [...active, ...closed].sort(
        (left, right) =>
          Date.parse(right.lastSeenAt) - Date.parse(left.lastSeenAt),
      );
    }

    return status === "closed"
      ? this.granular.listClosedSessions({ environmentId: this.environmentId })
      : this.granular.listOpenSessions({ environmentId: this.environmentId });
  }

  async getUserEnvironmentState(
    options: UserEnvironmentStateOptions = {},
  ): Promise<UserEnvironmentState> {
    return this.granular.getUserEnvironmentState({
      ...options,
      environmentId: this.environmentId,
    });
  }

  async markUserEnvironmentSessionsRead(
    options: MarkUserEnvironmentReadOptions,
  ): Promise<UserEnvironmentState["readAtBySessionId"]> {
    return this.granular.markUserEnvironmentSessionsRead({
      ...options,
      environmentId: this.environmentId,
    });
  }

  async createSession(
    options?: CreateSessionOptions,
  ): Promise<EnvironmentSession> {
    return this.granular.createSession({
      environmentId: this.environmentId,
      clientId: options?.clientId,
      initialHeap: options?.initialHeap,
    });
  }

  async connectSession(
    sessionId: string,
    options?: {
      clientId?: string;
      maxReconnectAttempts?: number;
      reconnectDelayMs?: number;
    },
  ): Promise<EnvironmentSession> {
    const session = await this.granular["connectSession"]({
      sessionId,
      clientId: options?.clientId,
      maxReconnectAttempts: options?.maxReconnectAttempts,
      reconnectDelayMs: options?.reconnectDelayMs,
    });
    if (session.environmentId !== this.environmentId) {
      await session.disconnect().catch(() => {
        session.disconnectTransport();
      });
      throw new Error(
        `Session ${sessionId} belongs to environment ${session.environmentId}, not ${this.environmentId}.`,
      );
    }
    return session;
  }

  async reopenSession(
    sessionId: string,
    options?: { clientId?: string },
  ): Promise<EnvironmentSession> {
    const session = await this.granular.reopenSession(sessionId, {
      clientId: options?.clientId,
    });
    if (session.environmentId !== this.environmentId) {
      await session.disconnect().catch(() => {
        session.disconnectTransport();
      });
      throw new Error(
        `Session ${sessionId} belongs to environment ${session.environmentId}, not ${this.environmentId}.`,
      );
    }
    return session;
  }

  async closeSession(
    sessionId: string,
    session?: EnvironmentSession | null,
  ): Promise<void> {
    await this.granular.closeSession(sessionId, session);
  }

  async listFeedback(): Promise<EnvironmentFeedbackRecord[]> {
    const response = await this.controlPlaneRequest<{
      items?: Array<{
        feedbackId: string;
        sessionId: string;
        jobId: string;
        sentiment: EnvironmentFeedbackRecord["sentiment"];
        comment?: string | null;
        metadata?: Record<string, unknown> | null;
        createdAt: string | null;
      }>;
    }>(`/control/environments/${this.environmentId}/feedback`);
    return Array.isArray(response.items) ? response.items : [];
  }

  private getRuntimeBaseUrl(): string {
    return deriveRuntimeBaseUrl(this._apiEndpoint);
  }

  private async controlPlaneRequest<T>(
    path: string,
    options: RequestInit = {},
  ): Promise<T> {
    const runtimeBase = this.getRuntimeBaseUrl();
    const url = `${runtimeBase}${path}`;

    const response = await fetch(url, {
      ...options,
      headers: {
        Authorization: `Bearer ${this._apiKey}`,
        "Content-Type": "application/json",
        Connection: "close",
        ...options.headers,
      },
    });

    if (!response.ok) {
      throw new Error(
        `Control Plane API Error (${response.status}): ${await response.text()}`,
      );
    }

    return response.json() as Promise<T>;
  }

  // ==================== ID ↔ GRAPH PATH MAPPING ====================

  private static normalizeGraphPathSegment(value: string): string {
    return value
      .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
      .replace(/[^A-Za-z0-9]+/g, "_")
      .replace(/_+/g, "_")
      .replace(/^_+|_+$/g, "")
      .toLowerCase();
  }

  /**
   * Convert a class name + application record ID into Granular's graph path.
   *
   * This mirrors the record-write path normalization used by the control plane.
   * Keep the original customer/system ID in `real_id`; graph paths are stable
   * internal addresses, not the source of truth for business identity.
   */
  static toGraphPath(className: string, id: string): string {
    return `${Environment.normalizeGraphPathSegment(className)}_${Environment.normalizeGraphPathSegment(id)}`;
  }

  /**
   * Best-effort extraction of an ID-like suffix from a graph path.
   *
   * Prefer the record's `real_id` field whenever exact customer/system IDs
   * matter, because graph path normalization is intentionally lossy.
   */
  static extractIdFromGraphPath(graphPath: string, className: string): string {
    const normalizedPrefix = `${Environment.normalizeGraphPathSegment(className)}_`;
    if (graphPath.startsWith(normalizedPrefix)) {
      return graphPath.substring(normalizedPrefix.length);
    }
    const prefix = `${className}_`;
    return graphPath.startsWith(prefix)
      ? graphPath.substring(prefix.length)
      : graphPath;
  }

  /**
   * Execute a GraphQL query against the environment's graph.
   *
   * The query uses the Granular graph query language (based on Cypher/GraphQL).
   * Authentication is handled automatically using the SDK's API key.
   *
   * @param query - The GraphQL query string
   * @param variables - Optional variables for the query
   * @returns The query result data
   *
   * @example
   * ```typescript
   * // Read the workspace
   * const result = await env.graphql(
   *   `query { model(path: "workspace") { path label submodels { path label } } }`
   * );
   * console.log(result.data);
   *
   * // Create a model
   * const created = await env.graphql(
   *   `mutation { at(path: "workspace") { create_submodel(subpath: "my_node", label: "My Node", prototype: "Model") { model { path label } } } }`
   * );
   * ```
   */
  async graphql<T = any>(
    query: string,
    variables?: Record<string, any>,
  ): Promise<GraphQLResult<T>> {
    const response = await fetch(this._apiEndpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${this._apiKey}`,
      },
      body: JSON.stringify({
        environmentId: this.environmentId,
        query,
        variables,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`GraphQL API Error (${response.status}): ${errorText}`);
    }

    return response.json() as Promise<GraphQLResult<T>>;
  }

  async searchRecords(
    query: string,
    options: RecordSearchOptions = {},
  ): Promise<RecordSearchResult[]> {
    const normalizedQuery = query.replace(/\s+/g, " ").trim();
    const limit = Math.max(1, Math.min(50, Math.floor(options.limit ?? 12)));
    const offset = Math.max(0, Math.floor(options.offset ?? 0));
    const response = await this.graphql<{
      record_search?: Array<{
        className?: string | null;
        model?: Record<string, any> | null;
      }> | null;
    }>(
      `
        query RecordMentionSearch(
          $query: String
          $limit: Int
          $offset: Int
          $classNames: [String!]
        ) {
          record_search(
            query: $query
            limit: $limit
            offset: $offset
            class_names: $classNames
          ) {
            className
            model {
              path
              label
              description
              submodels {
                path
                label
                string_value
                number_value
                boolean_value
              }
            }
          }
        }
      `,
      {
        query: normalizedQuery,
        limit,
        offset,
        classNames: options.classNames?.length ? options.classNames : [],
      },
    );

    const seen = new Set<string>();
    const results = (response.data?.record_search || []).flatMap((entry) => {
      const className = entry.className?.trim();
      const item =
        className && entry.model
          ? toRecordSearchResult(className, entry.model)
          : null;
      if (!item || seen.has(item.path)) {
        return [];
      }
      seen.add(item.path);
      return [item];
    });

    return results
      .map((result, index) => ({
        result,
        rank: rankRecordSearchResult(result, normalizedQuery, index),
      }))
      .sort((left, right) => left.rank - right.rank)
      .map((item) => item.result)
      .slice(0, limit);
  }

  // ==================== RELATIONSHIP METHODS ====================

  /**
   * Define a relationship between two model types.
   *
   * Creates both submodels (if they don't exist) and links them with
   * a RelationshipDef node that encodes cardinality.
   *
   * @example
   * ```typescript
   * // Author has many Books, Book has one Author
   * const rel = await env.defineRelationship({
   *   model: 'author',
   *   localSubmodel: 'books',
   *   localIsMany: true,
   *   foreignModel: 'book',
   *   foreignSubmodel: 'author',
   *   foreignIsMany: false,
   * });
   * console.log(rel.relationship_kind); // "one_to_many"
   * ```
   */
  async defineRelationship(
    options: DefineRelationshipOptions,
  ): Promise<RelationshipInfo> {
    const result = await this.graphql<{
      at: {
        define_relationship: RelationshipInfo;
      };
    }>(
      `mutation DefineRelationship(
                $localSubmodel: String!,
                $foreignModel: String!,
                $foreignSubmodel: String!,
                $localIsMany: Boolean!,
                $foreignIsMany: Boolean!,
                $name: String
            ) {
                at(path: "${options.model}") {
                    define_relationship(
                        local_submodel: $localSubmodel
                        foreign_model: $foreignModel
                        foreign_submodel: $foreignSubmodel
                        local_is_many: $localIsMany
                        foreign_is_many: $foreignIsMany
                        name: $name
                    ) {
                        name
                        local_submodel { path label }
                        local_is_many
                        foreign_submodel { path label }
                        foreign_is_many
                        foreign_model { path label }
                        relationship_kind
                    }
                }
            }`,
      {
        localSubmodel: options.localSubmodel,
        foreignModel: options.foreignModel,
        foreignSubmodel: options.foreignSubmodel,
        localIsMany: options.localIsMany,
        foreignIsMany: options.foreignIsMany,
        name: options.name,
      },
    );

    if (result.errors?.length) {
      throw new Error(
        `defineRelationship failed: ${result.errors[0]?.message ?? "Unknown GraphQL error"}`,
      );
    }

    return result.data!.at.define_relationship;
  }

  /**
   * Get all relationships for a model type.
   *
   * @param modelPath - The model type path (e.g., "author")
   * @returns Array of relationships from this model's perspective
   *
   * @example
   * ```typescript
   * const rels = await env.getRelationships('author');
   * for (const rel of rels) {
   *   console.log(`${rel.local_submodel.path} -> ${rel.foreign_model.path} (${rel.relationship_kind})`);
   * }
   * ```
   */
  async getRelationships(modelPath: string): Promise<RelationshipInfo[]> {
    const result = await this.graphql<{
      model: {
        relationships: RelationshipInfo[];
      };
    }>(
      `query GetRelationships($path: String) {
                model(path: $path) {
                    relationships {
                        name
                        local_submodel { path label }
                        local_is_many
                        foreign_submodel { path label }
                        foreign_is_many
                        foreign_model { path label }
                        relationship_kind
                    }
                }
            }`,
      { path: modelPath },
    );

    if (result.errors?.length) {
      throw new Error(
        `getRelationships failed: ${result.errors[0]?.message ?? "Unknown GraphQL error"}`,
      );
    }

    return result.data?.model?.relationships || [];
  }

  /**
   * Attach a target model to a relationship submodel.
   *
   * Handles cardinality automatically:
   * - "One" side: sets/replaces the reference
   * - "Many" side: adds the target to the collection
   *
   * If the target model doesn't exist, it's created as an instance of the foreign type.
   * Bidirectional sync is automatic.
   *
   * @param modelPath - The model instance path (e.g., "tolkien")
   * @param submodelPath - The relationship submodel (e.g., "books")
   * @param targetPath - The target model to attach (e.g., "lord_of_the_rings")
   *
   * @example
   * ```typescript
   * // Attach a book to an author (many side)
   * await env.attach('tolkien', 'books', 'lord_of_the_rings');
   * // This also automatically sets lord_of_the_rings:author -> tolkien
   * ```
   */
  async attach(
    modelPath: string,
    submodelPath: string,
    targetPath: string,
  ): Promise<void> {
    const result = await this.graphql(
      `mutation Attach($target: String!) {
                at(path: "${modelPath}") {
                    at(submodel: "${submodelPath}") {
                        attach(target: $target) {
                            model { path }
                        }
                    }
                }
            }`,
      { target: targetPath },
    );

    if (result.errors?.length) {
      throw new Error(
        `attach failed: ${result.errors[0]?.message ?? "Unknown GraphQL error"}`,
      );
    }
  }

  /**
   * Detach a target model from a relationship submodel.
   *
   * Handles bidirectional cleanup automatically.
   *
   * @param modelPath - The model instance path
   * @param submodelPath - The relationship submodel
   * @param targetPath - The target to detach (optional for "one" side; omit on "many" side to detach all)
   *
   * @example
   * ```typescript
   * // Detach a specific book
   * await env.detach('tolkien', 'books', 'lord_of_the_rings');
   *
   * // Detach all books
   * await env.detach('tolkien', 'books');
   * ```
   */
  async detach(
    modelPath: string,
    submodelPath: string,
    targetPath?: string,
  ): Promise<void> {
    const targetArg = targetPath ? `target: "${targetPath}"` : "";
    const result = await this.graphql(
      `mutation Detach {
                at(path: "${modelPath}") {
                    at(submodel: "${submodelPath}") {
                        detach(${targetArg}) {
                            model { path }
                        }
                    }
                }
            }`,
    );

    if (result.errors?.length) {
      throw new Error(
        `detach failed: ${result.errors[0]?.message ?? "Unknown GraphQL error"}`,
      );
    }
  }

  /**
   * List all related models through a relationship submodel.
   *
   * @param modelPath - The model instance path
   * @param submodelPath - The relationship submodel
   * @returns Array of related model references
   *
   * @example
   * ```typescript
   * const books = await env.listRelated('tolkien', 'books');
   * console.log(books); // [{ path: "lord_of_the_rings", label: "Lord of the Rings" }, ...]
   * ```
   */
  async listRelated(
    modelPath: string,
    submodelPath: string,
  ): Promise<ModelRef[]> {
    const result = await this.graphql<{
      at: {
        at: {
          list_related: ModelRef[];
        };
      };
    }>(
      `mutation ListRelated {
                at(path: "${modelPath}") {
                    at(submodel: "${submodelPath}") {
                        list_related { path label }
                    }
                }
            }`,
    );

    if (result.errors?.length) {
      throw new Error(
        `listRelated failed: ${result.errors[0]?.message ?? "Unknown GraphQL error"}`,
      );
    }

    return result.data?.at?.at?.list_related || [];
  }

  /**
   * Apply a manifest to the current environment's graph.
   *
   * Translates each manifest operation into GraphQL mutations and executes them
   * in order. This is the core mechanism for creating classes, fields, and
   * relationships from a declarative manifest.
   *
   * @param manifest - The manifest content to apply
   * @returns Summary of applied operations
   *
   * @example
   * ```typescript
   * await environment.applyManifest({
   *   schemaVersion: 2,
   *   name: 'my-app',
   *   volumes: [{
   *     name: 'schema',
   *     scope: 'sandbox',
   *     operations: [
   *       { create: 'author', extends: 'class', has: { name: { type: 'string' } } },
   *       { create: 'book', extends: 'class', has: { title: { type: 'string' } } },
   *       { defineRelationship: {
   *         left: 'author', right: 'book',
   *         leftSubmodel: 'books', rightSubmodel: 'author',
   *         leftIsMany: true, rightIsMany: false,
   *       }},
   *     ],
   *   }],
   * });
   * ```
   */
  async applyManifest(
    manifest: ManifestContent,
  ): Promise<{ applied: number; errors: string[] }> {
    const errors: string[] = [];
    let applied = 0;

    for (const volume of manifest.volumes) {
      // Build alias map from imports
      const aliasMap: Record<string, string> = {};

      // Resolve and apply imports first
      if (volume.imports) {
        for (const imp of volume.imports) {
          aliasMap[imp.alias] = imp.name;

          const builtinOps = BUILTIN_MODULES[imp.name];
          if (builtinOps) {
            for (const op of builtinOps) {
              try {
                await this._applyOperation(op, {});
                applied++;
              } catch (err: any) {
                // Ignore "already exists" errors from re-applying imports
                if (!err.message?.includes("already exists")) {
                  errors.push(
                    `Import ${imp.name} operation failed: ${err.message}`,
                  );
                }
              }
            }
          } else {
            errors.push(
              `Unknown module: "${imp.name}" (only built-in modules are supported)`,
            );
          }
        }
      }

      // Apply volume operations with alias resolution
      for (const op of volume.operations) {
        try {
          await this._applyOperation(op, aliasMap);
          applied++;
        } catch (err: any) {
          errors.push(`Operation failed: ${err.message}`);
        }
      }
    }

    return { applied, errors };
  }

  /**
   * Resolve an alias reference like "@std/class" → "class"
   * Strips the alias prefix, returning the bare model path.
   */
  private _resolveAlias(ref: string, aliasMap: Record<string, string>): string {
    if (!ref.startsWith("@")) return ref;
    const slashIdx = ref.indexOf("/");
    if (slashIdx === -1) return ref;
    const alias = ref.substring(0, slashIdx);
    const symbolName = ref.substring(slashIdx + 1);
    if (aliasMap[alias]) {
      return symbolName; // The import was applied, so the symbol exists at its bare name
    }
    return ref; // Unknown alias, return as-is
  }

  private async _runGraphql(query: string, label: string): Promise<any> {
    const result = await this.graphql(query);
    if (result.errors?.length) {
      throw new Error(
        `${label}: ${result.errors[0]?.message ?? "Unknown GraphQL error"}`,
      );
    }
    return result.data;
  }

  private async _applyFieldMetamodels(
    fieldPath: string,
    spec: FieldMetamodelManifestSpec,
  ): Promise<void> {
    for (const mutation of buildFieldMetamodelMutations(fieldPath, spec)) {
      await this._runGraphql(mutation.query, mutation.label);
    }
  }

  private async _applyModelMetamodels(
    modelPath: string,
    op: ManifestOperation,
  ): Promise<void> {
    for (const mutation of buildModelMetamodelMutations(modelPath, op)) {
      await this._runGraphql(mutation.query, mutation.label);
    }
  }

  private async _ensureWorkspaceToolsRoot(): Promise<void> {
    await this._runGraphql(
      `mutation { create_model(path: "workspace", label: "workspace") { model { path } } }`,
      "ensure workspace",
    ).catch((error: Error) => {
      if (!error.message.includes("already exists")) throw error;
    });

    await this._runGraphql(
      `mutation { at(path: "workspace") { create_submodel(subpath: "tools", label: "Tools") { model { path } } } }`,
      "ensure workspace:tools",
    ).catch((error: Error) => {
      if (!error.message.includes("already exists")) throw error;
    });

    await this._runGraphql(
      `mutation { at(path: "workspace:tools") { create_submodel(subpath: "declared", label: "Declared") { model { path } } } }`,
      "ensure workspace:tools:declared",
    ).catch((error: Error) => {
      if (!error.message.includes("already exists")) throw error;
    });
  }

  private async _storeEffectSchemas(
    toolPath: string,
    effect: ManifestEffectDeclaration,
  ): Promise<void> {
    await this._runGraphql(
      `mutation { at(path: ${JSON.stringify(toolPath)}) { create_submodel(subpath: "inputSchema", label: "inputSchema") { set_string_value(value: ${JSON.stringify(JSON.stringify(effect.inputSchema))}) { done } } } }`,
      `store inputSchema on ${toolPath}`,
    ).catch((error: Error) => {
      if (!error.message.includes("already exists")) throw error;
    });

    if (effect.outputSchema) {
      await this._runGraphql(
        `mutation { at(path: ${JSON.stringify(toolPath)}) { create_submodel(subpath: "outputSchema", label: "outputSchema") { set_string_value(value: ${JSON.stringify(JSON.stringify(effect.outputSchema))}) { done } } } }`,
        `store outputSchema on ${toolPath}`,
      ).catch((error: Error) => {
        if (!error.message.includes("already exists")) throw error;
      });
    }
  }

  private async _applyEffectMetamodels(
    toolPath: string,
    metamodels?: EffectMetamodelManifestSpec,
  ): Promise<void> {
    for (const mutation of buildEffectMetamodelMutations(
      toolPath,
      metamodels,
    )) {
      await this._runGraphql(mutation.query, mutation.label);
    }
  }

  private async _ensureWorkspaceStreamsRoot(): Promise<void> {
    await this._runGraphql(
      `mutation { create_model(path: "workspace", label: "workspace") { model { path } } }`,
      "ensure workspace",
    ).catch((error: Error) => {
      if (!error.message.includes("already exists")) throw error;
    });

    await this._runGraphql(
      `mutation { at(path: "workspace") { create_submodel(subpath: "streams", label: "Streams") { model { path } } } }`,
      "ensure workspace:streams",
    ).catch((error: Error) => {
      if (!error.message.includes("already exists")) throw error;
    });
  }

  private async _applyEventStreamDeclaration(
    stream: ManifestEventStreamDef,
  ): Promise<void> {
    await this._ensureWorkspaceStreamsRoot();
    const streamPath = `workspace:streams:${stream.name}`;

    await this._runGraphql(
      `mutation { at(path: "workspace:streams") { create_submodel(subpath: ${JSON.stringify(stream.name)}, label: ${JSON.stringify(stream.name)}) { model { path } } } }`,
      `create stream ${stream.name}`,
    ).catch((error: Error) => {
      if (!error.message.includes("already exists")) throw error;
    });

    if (stream.description) {
      await this._runGraphql(
        `mutation { at(path: ${JSON.stringify(streamPath)}) { set_description(description: ${JSON.stringify(stream.description)}) { done } } }`,
        `set stream description on ${streamPath}`,
      );
    }

    for (const eventType of stream.eventTypes) {
      const typePath = `${streamPath}:${eventType.name}`;
      await this._runGraphql(
        `mutation { at(path: ${JSON.stringify(streamPath)}) { create_submodel(subpath: ${JSON.stringify(eventType.name)}, label: ${JSON.stringify(eventType.name)}) { model { path } } } }`,
        `create event type ${eventType.name} on ${streamPath}`,
      ).catch((error: Error) => {
        if (!error.message.includes("already exists")) throw error;
      });

      if (eventType.description) {
        await this._runGraphql(
          `mutation { at(path: ${JSON.stringify(typePath)}) { set_description(description: ${JSON.stringify(eventType.description)}) { done } } }`,
          `set event type description on ${typePath}`,
        );
      }

      if (eventType.payloadSchema?.properties) {
        const fieldSpecs: Record<string, ManifestPropertySpec> = {};
        for (const [propName, propSchema] of Object.entries(
          eventType.payloadSchema.properties,
        )) {
          const schema = propSchema as Record<string, unknown>;
          fieldSpecs[propName] = {
            type: (schema.type as string) ?? "string",
            description: schema.description as string | undefined,
          };
        }
        await this._applyFields(typePath, fieldSpecs);
      }

      if (eventType.payloadSchema?.required?.length) {
        await this._runGraphql(
          `mutation { at(path: ${JSON.stringify(typePath)}) { create_submodel(subpath: "required", label: "required") { set_string_value(value: ${JSON.stringify(JSON.stringify(eventType.payloadSchema.required))}) { done } } } }`,
          `store required fields on ${typePath}`,
        ).catch((error: Error) => {
          if (!error.message.includes("already exists")) throw error;
        });
      }
    }
  }

  private async _applyEffectDeclaration(
    effect: ManifestEffectDeclaration,
    aliasMap: Record<string, string>,
  ): Promise<void> {
    let containerPath = "workspace:tools:declared";
    if (effect.attachedClass) {
      containerPath = this._resolveAlias(effect.attachedClass, aliasMap);
    } else {
      await this._ensureWorkspaceToolsRoot();
    }

    await this._runGraphql(
      `mutation { at(path: ${JSON.stringify(containerPath)}) { create_submodel(subpath: ${JSON.stringify(effect.name)}, label: ${JSON.stringify(effect.name)}) { model { path } } } }`,
      `create effect model ${containerPath}:${effect.name}`,
    ).catch((error: Error) => {
      if (!error.message.includes("already exists")) throw error;
    });

    const toolPath = `${containerPath}:${effect.name}`;
    if (effect.description) {
      await this._runGraphql(
        `mutation { at(path: ${JSON.stringify(toolPath)}) { set_description(description: ${JSON.stringify(effect.description)}) { done } } }`,
        `set effect description on ${toolPath}`,
      );
    }
    await this._storeEffectSchemas(toolPath, effect);
    await this._applyEffectMetamodels(toolPath, effect.metamodels);
  }

  /**
   * Apply a single manifest operation via GraphQL
   */
  private async _applyOperation(
    op: ManifestOperation,
    aliasMap: Record<string, string>,
  ): Promise<void> {
    // 1. Relationship definition (standalone operation)
    if (op.defineRelationship) {
      const rel = op.defineRelationship;
      await this.defineRelationship({
        model: this._resolveAlias(rel.left, aliasMap),
        localSubmodel: rel.leftSubmodel,
        localIsMany: rel.leftIsMany,
        foreignModel: this._resolveAlias(rel.right, aliasMap),
        foreignSubmodel: rel.rightSubmodel,
        foreignIsMany: rel.rightIsMany,
        name: rel.name,
      });
      return;
    }

    // 2. Create a new model (optionally extending another)
    if (op.create) {
      const label = op.create.charAt(0).toUpperCase() + op.create.slice(1);
      const extendsRef = op.extends
        ? this._resolveAlias(op.extends, aliasMap)
        : undefined;
      const instanceOfRef = op.instanceOf
        ? this._resolveAlias(op.instanceOf, aliasMap)
        : undefined;

      if (extendsRef) {
        // Create model that extends a parent class
        await this._runGraphql(
          `mutation { create_model(path: "${op.create}", label: "${label}") { model { path } } }`,
          `create model ${op.create}`,
        );
        await this._runGraphql(
          `mutation { at(path: "${op.create}") { add_superclass(superclass: "${extendsRef}") { model { path } } } }`,
          `add superclass ${extendsRef} to ${op.create}`,
        );
      } else if (instanceOfRef) {
        // Create an instance of a type
        await this._runGraphql(
          `mutation { at(path: "${instanceOfRef}") { instantiate(path: "${op.create}", label: "${label}") { model { path } } } }`,
          `instantiate ${op.create} from ${instanceOfRef}`,
        );
      } else {
        // Create a standalone model
        await this._runGraphql(
          `mutation { create_model(path: "${op.create}", label: "${label}") { model { path } } }`,
          `create model ${op.create}`,
        );
      }

      // Apply fields (has) if present
      if (op.has) {
        await this._applyFields(op.create, op.has);
      }
      await this._applyModelMetamodels(op.create, op);
      if (op.withEffect) {
        await this._applyEffectDeclaration(op.withEffect, aliasMap);
      }
      return;
    }

    // 3. Modify an existing model
    if (op.on) {
      const target = this._resolveAlias(op.on, aliasMap);
      if (op.has) {
        await this._applyFields(target, op.has);
      }
      await this._applyModelMetamodels(target, op);
      if (op.withEffect) {
        await this._applyEffectDeclaration(op.withEffect, aliasMap);
      }
      return;
    }

    if (op.withEffect) {
      await this._applyEffectDeclaration(op.withEffect, aliasMap);
    }

    if (op.defineEventStream) {
      await this._applyEventStreamDeclaration(op.defineEventStream);
    }
  }

  /**
   * Apply field definitions (has) to a model via GraphQL
   */
  private async _applyFields(
    modelPath: string,
    fields: Record<string, ManifestPropertySpec>,
  ): Promise<void> {
    for (const [fieldName, spec] of Object.entries(fields)) {
      const fieldLabel = fieldName.charAt(0).toUpperCase() + fieldName.slice(1);

      // Create the submodel (field) on the model
      await this._runGraphql(
        `mutation {
                    at(path: ${JSON.stringify(modelPath)}) {
                        create_submodel(subpath: ${JSON.stringify(fieldName)}, label: ${JSON.stringify(fieldLabel)}) {
                            model { path }
                        }
                    }
                }`,
        `create field ${modelPath}.${fieldName}`,
      ).catch((error: Error) => {
        if (!error.message.includes("already exists")) throw error;
      });

      // Set type prototype if provided (e.g. 'string', 'number', 'boolean')
      if (spec.type) {
        await this._runGraphql(
          `mutation {
                        at(path: ${JSON.stringify(modelPath)}) {
                            at(submodel: ${JSON.stringify(fieldName)}) {
                                add_prototype(prototype: ${JSON.stringify(spec.type)}) { done }
                            }
                        }
                    }`,
          `set prototype on ${modelPath}:${fieldName}`,
        );
      }

      // Set description if provided
      if (spec.description) {
        await this._runGraphql(
          `mutation {
                        at(path: ${JSON.stringify(modelPath)}) {
                            at(submodel: ${JSON.stringify(fieldName)}) {
                                set_description(description: ${JSON.stringify(spec.description)}) { done }
                            }
                        }
                    }`,
          `set description on ${modelPath}:${fieldName}`,
        );
      }

      // Set value if provided
      if (spec.value !== undefined && spec.value !== null) {
        if (typeof spec.value === "string") {
          await this._runGraphql(
            `mutation {
                            at(path: ${JSON.stringify(modelPath)}) {
                                at(submodel: ${JSON.stringify(fieldName)}) {
                                    set_string_value(value: ${JSON.stringify(spec.value)}) { done }
                                }
                            }
                        }`,
            `set string value on ${modelPath}:${fieldName}`,
          );
        } else if (typeof spec.value === "number") {
          await this._runGraphql(
            `mutation {
                            at(path: ${JSON.stringify(modelPath)}) {
                                at(submodel: ${JSON.stringify(fieldName)}) {
                                    set_number_value(value: ${spec.value}) { done }
                                }
                            }
                        }`,
            `set number value on ${modelPath}:${fieldName}`,
          );
        } else if (typeof spec.value === "boolean") {
          await this._runGraphql(
            `mutation {
                            at(path: ${JSON.stringify(modelPath)}) {
                                at(submodel: ${JSON.stringify(fieldName)}) {
                                    set_boolean_value(value: ${spec.value}) { done }
                                }
                            }
                        }`,
            `set boolean value on ${modelPath}:${fieldName}`,
          );
        }
      }

      // Set reference if provided
      if (spec.ref) {
        await this._runGraphql(
          `mutation {
                        at(path: ${JSON.stringify(modelPath)}) {
                            at(submodel: ${JSON.stringify(fieldName)}) {
                                set_reference(reference: ${JSON.stringify(spec.ref)}) { done }
                            }
                        }
                    }`,
          `set reference on ${modelPath}:${fieldName}`,
        );
      }

      await this._applyFieldMetamodels(`${modelPath}:${fieldName}`, spec);

      // Recurse into nested has
      if (spec.has) {
        await this._applyFields(`${modelPath}:${fieldName}`, spec.has);
      }
    }
  }

  // ==================== RECORD OBJECT ====================

  /**
   * Create or update an instance of a class in the graph.
   *
   * Uses `instantiate` under the hood, which has find-or-create semantics:
   * if an instance with the given `id` already exists for the class it is
   * returned; otherwise a new instance is created.  Fields are then set
   * (overwriting previous values) and relationships are attached.
   *
   * The graph path is derived as `{className}_{id}` to ensure uniqueness
   * across classes (two objects of different classes may share the same
   * real-world ID).  Relationship targets are also resolved automatically
   * using the foreign class from the relationship definition.
   *
   * @param options - The object specification
   * @returns The graph path, real-world ID, and creation status
   *
   * @example
   * ```typescript
   * // Create an author with fields
   * const result = await env.recordObject({
   *   className: 'author',
   *   id: 'tolkien',
   *   label: 'J.R.R. Tolkien',
   *   fields: { name: 'J.R.R. Tolkien', birth_year: 1892 },
   *   relationships: { books: ['lotr', 'silmarillion'] },
   * });
   * // result.path → 'author_tolkien'  (internal graph path)
   * // result.id   → 'tolkien'         (real-world ID)
   * // result.created → true
   * ```
   */
  async recordObject(
    options: RecordObjectOptions,
  ): Promise<RecordObjectResult> {
    const results = await this.recordObjects([options]);
    const result = results[0];
    if (!result) {
      throw new Error("recordObject: no result returned for record");
    }
    return result;
  }

  /**
   * Batch version of `recordObject()`.
   *
   * Sends rows through the control-plane **`/records/batch`** endpoint in **chunks** (default
   * **100** records per HTTP request) so individual requests stay bounded and gateway timeouts are
   * unlikely. Each chunk is retried on transient network / worker errors.
   *
   * Use the optional second argument to:
   * - **`batchSize`** — rows per POST (smaller = more progress events; larger = fewer round trips).
   * - **`concurrency`** — run up to N chunk POSTs in parallel (capped at 16) when you want lower wall time.
   * - **`onChunkComplete`** — hook for UIs after each chunk succeeds (row order in the returned array
   *   always matches `records`; chunk **completion** order may differ when `concurrency > 1`).
   *
   * For **asynchronous** ingestion with worker-side batching and aggregate counters (`queued`,
   * `completed`, …), use **`enqueueRecordImport`** and poll **`getRecordImport`** /
   * **`getRecordImportSummary`** — best for very large fire-and-forget loads when immediate
   * synchronous commit of every row is not required.
   */
  async recordObjects(
    records: RecordObjectOptions[],
    options?: RecordObjectsOptions,
  ): Promise<RecordObjectResult[]> {
    if (!Array.isArray(records) || records.length === 0) {
      return [];
    }

    const batchSize = Math.max(
      1,
      Math.min(
        records.length,
        options?.batchSize ?? DEFAULT_DIRECT_RECORD_OBJECTS_REQUEST_BATCH_SIZE,
      ),
    );
    const concurrency = Math.min(
      MAX_RECORD_OBJECTS_CONCURRENCY,
      Math.max(1, options?.concurrency ?? 1),
    );

    const plans = planRecordObjectsChunks(records, batchSize);
    const total = records.length;
    const results: RecordObjectResult[] = new Array(total);
    const onChunk = options?.onChunkComplete;

    for (
      let waveStart = 0;
      waveStart < plans.length;
      waveStart += concurrency
    ) {
      const wave = plans.slice(waveStart, waveStart + concurrency);
      await Promise.all(
        wave.map(async (plan) => {
          const { items, durationMs } = await this.executeRecordObjectsChunk(
            plan.slice,
          );
          if (items.length !== plan.slice.length) {
            throw new Error(
              `recordObjects: chunk ${plan.chunkIndex + 1} returned ${items.length} results, expected ${plan.slice.length}`,
            );
          }
          for (let index = 0; index < items.length; index += 1) {
            const item = items[index];
            if (!item) {
              throw new Error(
                `recordObjects: chunk ${plan.chunkIndex + 1} returned an empty result at index ${index}`,
              );
            }
            results[plan.offset + index] = item;
          }
          if (onChunk) {
            const info: RecordObjectsChunkInfo = {
              chunkIndex: plan.chunkIndex,
              totalChunks: plan.chunkCount,
              offset: plan.offset,
              recordCount: plan.slice.length,
              durationMs,
              results: items,
            };
            await onChunk(info);
          }
        }),
      );
    }

    return results;
  }

  private async executeRecordObjectsChunk(
    chunk: RecordObjectOptions[],
  ): Promise<{ items: RecordObjectResult[]; durationMs: number }> {
    const wallStart = Date.now();
    let lastError: unknown;

    for (
      let attempt = 1;
      attempt <= DEFAULT_DIRECT_RECORD_OBJECTS_RETRY_COUNT;
      attempt += 1
    ) {
      try {
        const response = await this.controlPlaneRequest<{
          items: RecordObjectResult[];
          count: number;
        }>(`/control/environments/${this.environmentId}/records/batch`, {
          method: "POST",
          body: JSON.stringify({ records: chunk }),
        });

        const items = Array.isArray(response.items) ? response.items : [];
        return { items, durationMs: Date.now() - wallStart };
      } catch (error) {
        lastError = error;
        if (
          !isRetryableRecordObjectsError(error) ||
          attempt === DEFAULT_DIRECT_RECORD_OBJECTS_RETRY_COUNT
        ) {
          throw error;
        }
        await sleep(DEFAULT_DIRECT_RECORD_OBJECTS_RETRY_DELAY_MS * attempt);
      }
    }

    throw lastError instanceof Error ? lastError : new Error(String(lastError));
  }

  /**
   * Queue a background record import for this environment (async worker pipeline).
   *
   * **vs `recordObjects`:** this path accepts the full payload in one request, returns an
   * **`importId`**, and processes rows in the background — use **`getRecordImport`** /
   * **`getRecordImportSummary`** for progress. Choose it for large bulk loads where you do not
   * need every row committed before the HTTP call returns. Use **`recordObjects`** when you need
   * synchronous upserts and fine-grained chunk progress via **`onChunkComplete`**.
   */
  async enqueueRecordImport(
    records: RecordObjectOptions[],
    options: RecordImportOptions = {},
  ): Promise<RecordImport> {
    return this.controlPlaneRequest<RecordImport>(
      `/control/environments/${this.environmentId}/record-imports`,
      {
        method: "POST",
        body: JSON.stringify({
          records,
          batchSize: options.batchSize,
          setupRunId: options.setupRunId,
          writeMode: options.writeMode,
        }),
      },
    );
  }

  /**
   * List queued or completed record imports for this environment.
   */
  async listRecordImports(
    status?: RecordImportStatus,
  ): Promise<RecordImport[]> {
    const suffix = status ? `?status=${encodeURIComponent(status)}` : "";
    const response = await this.controlPlaneRequest<{ items: RecordImport[] }>(
      `/control/environments/${this.environmentId}/record-imports${suffix}`,
    );
    return Array.isArray(response.items) ? response.items : [];
  }

  /**
   * Fetch the latest aggregate import counters for this environment.
   */
  async getRecordImportSummary(): Promise<EnvironmentRecordImportSummary> {
    return this.controlPlaneRequest<EnvironmentRecordImportSummary>(
      `/control/environments/${this.environmentId}/record-imports/summary`,
    );
  }

  /**
   * Convenience helper returning queued + processing records for this environment.
   */
  async getAwaitingRecordCount(): Promise<number> {
    const summary = await this.getRecordImportSummary();
    return summary.awaitingRecords;
  }

  /**
   * Fetch a single record import by id.
   */
  async getRecordImport(importId: string): Promise<RecordImport> {
    return this.controlPlaneRequest<RecordImport>(
      `/control/record-imports/${importId}`,
    );
  }

  /**
   * Cancel a queued/background record import.
   */
  async cancelRecordImport(importId: string): Promise<RecordImport> {
    return this.controlPlaneRequest<RecordImport>(
      `/control/record-imports/${importId}/cancel`,
      {
        method: "POST",
      },
    );
  }
}

/**
 * Live runtime session attached to one opened environment.
 *
 * This is the object returned by `environment.sessions.create()` and friends.
 * It owns websocket state, prompts, job execution, and the synced Automerge
 * document while delegating environment-level data APIs back to
 * `session.environment`.
 */
export class EnvironmentSession extends Session {
  readonly environment: Environment;
  private readonly sessionDataRoutePrefix: string;

  /** The last known graph container status, updated by checkReadiness() or on heartbeat */
  graphContainerStatus: {
    lastKeepAliveAt: number;
    status: "warming" | "hot" | "unknown";
  } | null = null;

  constructor(
    client: WSClient,
    environment: Environment,
    clientId: string,
    options: {
      sessionDataRoutePrefix?: string;
      initialQuota?: GranularQuotaProgress | null;
    } = {},
  ) {
    super(client, clientId, { initialQuota: options.initialQuota });
    this.environment = environment;
    this.sessionDataRoutePrefix =
      options.sessionDataRoutePrefix || "/orchestrator/ws/sessions";
  }

  get environmentId(): string {
    return this.environment.environmentId;
  }

  get sandboxId(): string {
    return this.environment.sandboxId;
  }

  get ontologyId(): string {
    return this.environment.ontologyId;
  }

  get subjectId(): string {
    return this.environment.subjectId;
  }

  get envName(): string {
    return this.environment.envName;
  }

  get tag(): string | null {
    return this.environment.tag;
  }

  get versionId(): string {
    return this.environment.versionId;
  }

  get granularId(): string {
    return this.environment.granularId;
  }

  get permissionProfileId(): string {
    return this.environment.permissionProfileId;
  }

  get apiEndpoint(): string {
    return this.environment.apiEndpoint;
  }

  get data() {
    return this.environment.data;
  }

  get feedback() {
    return this.environment.feedback;
  }

  /**
   * Return a plain JS copy of the synced session heap.
   */
  getHeap(): SessionHeapSnapshot {
    const doc = this.document as Record<string, unknown> & {
      heap?: SessionHeapSnapshot;
    };
    return normalizeHeapSnapshot(doc?.heap);
  }

  private buildSessionDataUrl(path: string, query?: object): string {
    const searchParams = new URLSearchParams();
    for (const [key, value] of Object.entries(query || {}) as Array<
      [string, string | number | null | undefined]
    >) {
      if (value !== null && typeof value !== "undefined" && value !== "") {
        searchParams.set(key, String(value));
      }
    }
    const queryString = searchParams.toString();
    return `${this.environment.runtimeBaseUrl}${this.sessionDataRoutePrefix}/${encodeURIComponent(this.sessionId)}${path}${queryString ? `?${queryString}` : ""}`;
  }

  private async sessionDataFetch(
    path: string,
    query?: object,
    init: {
      method?: string;
      body?: SessionDataBody;
      headers?: SessionDataHeaders;
    } = {},
  ): Promise<Response> {
    const url = this.buildSessionDataUrl(path, query);

    for (
      let attempt = 1;
      attempt <= SESSION_DATA_REQUEST_RETRY_COUNT;
      attempt += 1
    ) {
      try {
        const headers = new Headers(init.headers);
        headers.set("Authorization", `Bearer ${this.environment.authToken}`);
        const response = await fetch(url, {
          method: init.method || "GET",
          headers,
          ...(typeof init.body === "undefined" ? {} : { body: init.body }),
        });

        if (response.ok) {
          return response;
        }

        const errorText = await response.text();
        const error = new Error(
          `Session data API Error (${response.status}): ${errorText}`,
        );
        if (
          isLocalControlUrl(url) &&
          isRetryableSessionDataError(error) &&
          attempt < SESSION_DATA_REQUEST_RETRY_COUNT
        ) {
          await sleep(SESSION_DATA_REQUEST_RETRY_DELAY_MS * attempt);
          continue;
        }
        throw error;
      } catch (error) {
        if (
          isLocalControlUrl(url) &&
          isRetryableSessionDataError(error) &&
          attempt < SESSION_DATA_REQUEST_RETRY_COUNT
        ) {
          await sleep(SESSION_DATA_REQUEST_RETRY_DELAY_MS * attempt);
          continue;
        }
        throw error;
      }
    }

    throw new Error(`Session data API Error: exhausted retries for ${url}`);
  }

  private async sessionDataRequest<T>(
    path: string,
    query?: object,
    init: {
      method?: string;
      body?: unknown;
    } = {},
  ): Promise<T> {
    const body =
      typeof init.body === "undefined" ? undefined : JSON.stringify(init.body);
    const response = await this.sessionDataFetch(path, query, {
      method: init.method || "GET",
      headers: { "Content-Type": "application/json" },
      ...(typeof body === "undefined" ? {} : { body }),
    });
    return response.json() as Promise<T>;
  }

  private async collectAllSessionItems<T>(
    listPage: (options: {
      limit?: number;
      cursor?: string | null;
    }) => Promise<SessionCollectionListResult<T>>,
  ): Promise<T[]> {
    const items: T[] = [];
    let cursor: string | null = null;
    do {
      const page = await listPage({ limit: 500, cursor });
      items.push(...page.items);
      cursor = page.nextCursor;
    } while (cursor);
    return items;
  }

  /**
   * Fetch the live session document from the runtime DO.
   *
   * For history and saved artifacts, prefer the collection APIs on
   * `messages`, `timeline`, `jobs`, and `heap`.
   */
  async getDocument(): Promise<SessionDocumentResult> {
    return this.sessionDataRequest<SessionDocumentResult>("/document");
  }

  get messages() {
    return {
      list: (options: SessionCollectionListOptions = {}) =>
        this.sessionDataRequest<
          SessionCollectionListResult<SessionConversationMessage>
        >("/messages", options),
    };
  }

  get timeline() {
    return {
      list: (options: SessionCollectionListOptions = {}) =>
        this.sessionDataRequest<
          SessionCollectionListResult<SessionTimelineEvent>
        >("/timeline", options),
    };
  }

  get jobs() {
    return {
      list: (options: SessionJobListOptions = {}) =>
        this.sessionDataRequest<SessionCollectionListResult<SessionJobRecord>>(
          "/jobs",
          options,
        ),
      get: (jobId: string) =>
        this.sessionDataRequest<SessionJobRecord>(
          `/jobs/${encodeURIComponent(jobId)}`,
        ),
    };
  }

  get files() {
    return {
      list: (
        options: SessionCollectionListOptions & {
          status?: string | null;
          source?: string | null;
        } = {},
      ) =>
        this.sessionDataRequest<SessionCollectionListResult<SessionFileRecord>>(
          "/files",
          options,
        ),
      get: (fileId: string) =>
        this.sessionDataRequest<SessionFileRecord>(
          `/files/${encodeURIComponent(fileId)}`,
        ),
      upload: async (
        body: SessionFileUploadBody,
        options: SessionFileUploadOptions = {},
      ): Promise<SessionFileRecord> => {
        const headers = new Headers({
          "Content-Type":
            options.contentType ||
            contentTypeFromUploadBody(body) ||
            "application/octet-stream",
          "x-granular-filename":
            options.filename || filenameFromUploadBody(body) || "upload",
          "x-granular-file-source": options.source || "sdk",
        });
        if (options.parentFileIds?.length) {
          headers.set(
            "x-granular-parent-file-ids",
            JSON.stringify(options.parentFileIds),
          );
        }
        if (options.metadata) {
          headers.set(
            "x-granular-file-metadata",
            JSON.stringify(options.metadata),
          );
        }
        const response = await this.sessionDataFetch("/files", undefined, {
          method: "POST",
          headers,
          body: bodyInitFromSessionFileUpload(body),
        });
        return response.json() as Promise<SessionFileRecord>;
      },
      download: async (fileId: string): Promise<ArrayBuffer> => {
        const response = await this.sessionDataFetch(
          `/files/${encodeURIComponent(fileId)}/content`,
        );
        return response.arrayBuffer();
      },
      delete: (fileId: string) =>
        this.sessionDataRequest<{ deleted: boolean; fileId: string }>(
          `/files/${encodeURIComponent(fileId)}`,
          undefined,
          { method: "DELETE" },
        ),
    };
  }

  get heap() {
    return {
      entries: {
        list: (options: SessionCollectionListOptions = {}) =>
          this.sessionDataRequest<
            SessionCollectionListResult<SessionHeapEntry>
          >("/heap/entries", options),
        get: (path: string) =>
          this.sessionDataRequest<SessionHeapEntry>(
            `/heap/entries/${encodeURIComponent(path)}`,
          ),
      },
      lists: {
        list: (options: SessionCollectionListOptions = {}) =>
          this.sessionDataRequest<SessionCollectionListResult<SessionHeapList>>(
            "/heap/lists",
            options,
          ),
        get: (name: string) =>
          this.sessionDataRequest<SessionHeapList>(
            `/heap/lists/${encodeURIComponent(name)}`,
          ),
      },
      variables: {
        list: (options: SessionCollectionListOptions = {}) =>
          this.sessionDataRequest<
            SessionCollectionListResult<SessionHeapVariable>
          >("/heap/variables", options),
        get: (name: string) =>
          this.sessionDataRequest<SessionHeapVariable>(
            `/heap/variables/${encodeURIComponent(name)}`,
          ),
        delete: (name: string) =>
          this.sessionDataRequest<{ deleted: boolean; name: string }>(
            `/heap/variables/${encodeURIComponent(name)}`,
            undefined,
            { method: "DELETE" },
          ),
      },
    };
  }

  get transcript() {
    return {
      list: async (
        options: SessionCollectionListOptions = {},
      ): Promise<SessionCollectionListResult<SessionTranscriptEntry>> => {
        const [messages, jobs, entries, lists] = await Promise.all([
          this.collectAllSessionItems(this.messages.list),
          this.collectAllSessionItems((pageOptions) =>
            this.jobs.list({ ...pageOptions, status: "all" }),
          ),
          this.collectAllSessionItems(this.heap.entries.list),
          this.collectAllSessionItems(this.heap.lists.list),
        ]);
        const liveDoc = {
          conversation: { messages },
          jobs: {
            byId: Object.fromEntries(
              jobs
                .map((job) => {
                  const record =
                    job && typeof job === "object"
                      ? (job as Record<string, unknown>)
                      : null;
                  const id =
                    typeof record?.jobId === "string"
                      ? record.jobId
                      : typeof record?.id === "string"
                        ? record.id
                        : null;
                  return id ? [id, record] : null;
                })
                .filter((entry): entry is [string, Record<string, unknown>] =>
                  Boolean(entry),
                ),
            ),
          },
        };
        const heap = normalizeHeapSnapshot({
          entriesByPath: Object.fromEntries(
            entries
              .map((entry) => {
                return entry?.path
                  ? [entry.path, entry as unknown as Record<string, unknown>]
                  : null;
              })
              .filter((entry): entry is [string, Record<string, unknown>] =>
                Boolean(entry),
              ),
          ),
          listsByName: Object.fromEntries(
            lists
              .map((list) => {
                return list?.name
                  ? [list.name, list as unknown as Record<string, unknown>]
                  : null;
              })
              .filter((entry): entry is [string, Record<string, unknown>] =>
                Boolean(entry),
              ),
          ),
          variablesByName: this.getHeap().variablesByName,
          updatedAt: Date.now(),
        });
        const allItems = buildSessionTranscript({
          liveDoc,
          sessionHeap: heap,
        });
        const limit = Math.max(
          1,
          Math.min(500, Math.floor(options.limit ?? 100)),
        );
        const offset =
          typeof options.cursor === "string"
            ? Number.parseInt(options.cursor, 10) || 0
            : 0;
        const items = allItems.slice(offset, offset + limit);
        const nextOffset = offset + items.length;
        return {
          items,
          nextCursor: nextOffset < allItems.length ? String(nextOffset) : null,
          totalCount: allItems.length,
        };
      },
    };
  }

  async graphql<T = any>(
    query: string,
    variables?: Record<string, any>,
  ): Promise<GraphQLResult<T>> {
    return this.environment.graphql(query, variables);
  }

  async searchRecords(
    query: string,
    options: RecordSearchOptions = {},
  ): Promise<RecordSearchResult[]> {
    return this.environment.searchRecords(query, options);
  }

  async mentionRecord(input: RecordMentionInput): Promise<SessionHeapEntry> {
    return this.sessionDataRequest<SessionHeapEntry>(
      "/records/mention",
      undefined,
      {
        method: "POST",
        body: input,
      },
    );
  }

  async defineRelationship(
    options: DefineRelationshipOptions,
  ): Promise<RelationshipInfo> {
    return this.environment.defineRelationship(options);
  }

  async getRelationships(modelPath: string): Promise<RelationshipInfo[]> {
    return this.environment.getRelationships(modelPath);
  }

  async attach(
    modelPath: string,
    submodelPath: string,
    targetPath: string,
  ): Promise<void> {
    return this.environment.attach(modelPath, submodelPath, targetPath);
  }

  async detach(
    modelPath: string,
    submodelPath: string,
    targetPath?: string,
  ): Promise<void> {
    return this.environment.detach(modelPath, submodelPath, targetPath);
  }

  async listRelated(
    modelPath: string,
    submodelPath: string,
  ): Promise<ModelRef[]> {
    return this.environment.listRelated(modelPath, submodelPath);
  }

  async applyManifest(
    manifest: ManifestContent,
  ): Promise<{ applied: number; errors: string[] }> {
    return this.environment.applyManifest(manifest);
  }

  async recordObject(
    options: RecordObjectOptions,
  ): Promise<RecordObjectResult> {
    return this.environment.recordObject(options);
  }

  async recordObjects(
    records: RecordObjectOptions[],
    options?: RecordObjectsOptions,
  ): Promise<RecordObjectResult[]> {
    return this.environment.recordObjects(records, options);
  }

  async enqueueRecordImport(
    records: RecordObjectOptions[],
    options: RecordImportOptions = {},
  ): Promise<RecordImport> {
    return this.environment.enqueueRecordImport(records, options);
  }

  async listRecordImports(
    status?: RecordImportStatus,
  ): Promise<RecordImport[]> {
    return this.environment.listRecordImports(status);
  }

  async getRecordImportSummary(): Promise<EnvironmentRecordImportSummary> {
    return this.environment.getRecordImportSummary();
  }

  async getAwaitingRecordCount(): Promise<number> {
    return this.environment.getAwaitingRecordCount();
  }

  async getRecordImport(importId: string): Promise<RecordImport> {
    return this.environment.getRecordImport(importId);
  }

  async cancelRecordImport(importId: string): Promise<RecordImport> {
    return this.environment.cancelRecordImport(importId);
  }

  async listFeedback(): Promise<EnvironmentFeedbackRecord[]> {
    return this.environment.listFeedback();
  }

  /**
   * Close the session and disconnect from the sandbox.
   *
   * Sends `client.goodbye` over WebSocket first, then issues an HTTP fallback
   * to the runtime goodbye endpoint if no definitive WS-side runtime notify
   * acknowledgement was observed.
   */
  override async disconnect(): Promise<void> {
    let wsNotifiedRuntime = false;

    try {
      const goodbye = await this.rpc<{ ok?: boolean; via?: string }>(
        "client.goodbye",
        {
          timestamp: Date.now(),
        },
      );
      wsNotifiedRuntime = Boolean(goodbye?.ok && goodbye?.via);
    } catch {
      wsNotifiedRuntime = false;
    }

    if (!wsNotifiedRuntime) {
      try {
        await fetch(
          `${this.environment.runtimeBaseUrl}/orchestrator/runtime/environments/${this.environmentId}/session-goodbye`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${this.environment.authToken}`,
              Connection: "close",
            },
            body: JSON.stringify({
              reason: "sdk_disconnect_http_fallback",
              sessionId: this.client.currentSessionId,
            }),
          },
        );
      } catch {
        // Best effort fallback.
      }
    }

    this.client.disconnect();
  }

  /**
   * Close only the socket transport without sending `client.goodbye`.
   */
  disconnectTransport(): void {
    this.client.disconnect();
  }

  /**
   * Backwards-compatible alias for `disconnect()`.
   */
  async close(): Promise<void> {
    await this.disconnect();
  }

  /**
   * Check if the graph container is ready and warm.
   */
  async checkReadiness(): Promise<{
    lastKeepAliveAt: number;
    status: "warming" | "hot" | "unknown";
  }> {
    const result = (await this.client.call("client.heartbeat", {})) as {
      status?: string;
      timestamp?: number;
      graphContainerStatus?: {
        lastKeepAliveAt: number;
        status: "warming" | "hot" | "unknown";
      };
    };

    const containerStatus = result?.graphContainerStatus ?? {
      lastKeepAliveAt: Date.now(),
      status: "unknown" as const,
    };

    this.graphContainerStatus = containerStatus;
    this.emit("readiness", containerStatus);
    return containerStatus;
  }
}

export class OntologyHandle {
  private granular: Granular;
  private ontologyNameOrId: string;

  constructor(granular: Granular, ontologyNameOrId: string) {
    this.granular = granular;
    this.ontologyNameOrId = ontologyNameOrId;
  }

  get effects() {
    return {
      register: async (effect: ToolWithHandler): Promise<void> =>
        this.granular.registerEffect(this.ontologyNameOrId, effect),
      registerMany: async (effects: ToolWithHandler[]): Promise<void> =>
        this.granular.registerEffects(this.ontologyNameOrId, effects),
      unregister: async (name: string): Promise<void> =>
        this.granular.unregisterEffect(this.ontologyNameOrId, name),
      clear: async (): Promise<void> =>
        this.granular.unregisterAllEffects(this.ontologyNameOrId),
      disconnect: async (): Promise<void> =>
        this.granular.disconnectEffects(this.ontologyNameOrId),
    };
  }

  get importer() {
    return {
      onEnvironmentCreate: (handler: EnvironmentImporterHandler): void =>
        this.granular.registerEnvironmentImporter(
          this.ontologyNameOrId,
          handler,
        ),
      clear: (): void =>
        this.granular.clearEnvironmentImporter(this.ontologyNameOrId),
    };
  }
}

export class Granular {
  private apiKey: string;
  private apiUrl: string;
  private httpUrl: string;
  private tokenProvider?: AccessTokenProvider;
  private WebSocketCtor?: any;
  private onUnexpectedClose?: (info: WSDisconnectInfo) => void;
  private onReconnectError?: (info: WSReconnectErrorInfo) => void;
  private effectHostUrl?: string;
  private debugHttp = process.env.GRANULAR_DEBUG_HTTP === "1";

  /** Sandbox-level effect registry: sandboxId → (effectKey@selector → ToolWithHandler) */
  private sandboxEffects: Map<string, Map<string, ToolWithHandler>> = new Map();
  /** Live sandbox-scoped effect hosts keyed by sandboxId */
  private sandboxEffectHosts: Map<string, SandboxEffectHost> = new Map();
  /** In-flight host connection promises to avoid duplicate concurrent connects */
  private sandboxEffectHostPromises: Map<string, Promise<SandboxEffectHost>> =
    new Map();
  /** Ontology-bound environment importer hooks keyed by the caller's ontology identifier. */
  private ontologyImporters: Map<string, EnvironmentImporterHandler> =
    new Map();
  /** Resolved importer hooks keyed by sandboxId for fast lookups during openEnvironment(). */
  private sandboxImporters: Map<string, ResolvedEnvironmentImporter> =
    new Map();

  /**
   * Create a new Granular client
   * @param options - Client configuration
   */
  constructor(options: GranularOptions) {
    const auth = options.token ?? options.apiKey;
    if (!auth) {
      throw new Error(
        "Granular client requires either apiKey or token. Set GRANULAR_API_KEY or GRANULAR_TOKEN, or pass one in options.",
      );
    }
    this.apiUrl = resolveApiUrl(options.apiUrl, options.endpointMode);
    this.apiKey = resolveAuthTokenForApiUrl(auth, this.apiUrl);
    this.tokenProvider = options.tokenProvider;
    this.WebSocketCtor = options.WebSocketCtor;
    this.onUnexpectedClose = options.onUnexpectedClose;
    this.onReconnectError = options.onReconnectError;
    this.effectHostUrl = options.effectHostUrl;
    // Convert WebSocket URL to HTTP URL for REST API
    this.httpUrl = this.apiUrl
      .replace(/^wss:\/\//, "https://")
      .replace(/^ws:\/\//, "http://")
      .replace(/\/ws$/, "");
  }

  /**
   * Return an ontology-scoped handle for effects and other ontology-level APIs.
   */
  ontology(ontologyNameOrId: string): OntologyHandle {
    return new OntologyHandle(this, ontologyNameOrId);
  }

  registerEnvironmentImporter(
    ontologyNameOrId: string,
    handler: EnvironmentImporterHandler,
  ): void {
    this.ontologyImporters.set(ontologyNameOrId, handler);
    if (ontologyNameOrId.startsWith("sbx_")) {
      this.sandboxImporters.set(ontologyNameOrId, {
        handler,
        sourceOntology: ontologyNameOrId,
      });
      return;
    }

    for (const [sandboxId, importer] of this.sandboxImporters.entries()) {
      if (importer.sourceOntology === ontologyNameOrId) {
        this.sandboxImporters.set(sandboxId, {
          handler,
          sourceOntology: ontologyNameOrId,
        });
      }
    }
  }

  clearEnvironmentImporter(ontologyNameOrId: string): void {
    this.ontologyImporters.delete(ontologyNameOrId);

    for (const [sandboxId, importer] of this.sandboxImporters.entries()) {
      if (importer.sourceOntology === ontologyNameOrId) {
        this.sandboxImporters.delete(sandboxId);
      }
    }
  }

  /**
   * Records/upserts a user and prepares them for sandbox connections
   *
   * @param options - User options
   * @returns The recorded user with both `userId` and `granularId`
   *
   * @example
   * ```typescript
   * const user = await granular.recordUser({
   *   userId: 'user_123',
   *   name: 'John Doe',
   *   permissions: ['agent'],
   * });
   * ```
   */
  async recordUser(options: RecordUserOptions): Promise<User> {
    // Create or get subject via control plane
    const subject = normalizeSubject(
      await this.request<Subject>("/control/subjects", {
        method: "POST",
        body: JSON.stringify({
          identityId: options.userId,
          name: options.name,
          email: options.email,
        }),
      }),
    );

    return normalizeUser({
      granularId: subject.granularId,
      userId: options.userId,
      subjectId: subject.subjectId,
      identityId: subject.identityId,
      name: options.name || subject.name || undefined,
      email: options.email || subject.email || undefined,
      permissions: options.permissions || [],
    });
  }

  /**
   * Alias for `recordUser()` with user-facing naming that matches upsert semantics.
   */
  async upsertUser(options: RecordUserOptions): Promise<User> {
    return this.recordUser(options);
  }

  private async resolveConnectUser(
    options: OpenEnvironmentOptions | ConnectOptions,
  ): Promise<ResolvedConnectUser> {
    const providedIdentityCount = [
      Boolean(options.user),
      Boolean(options.userId),
      Boolean(options.granularId),
    ].filter(Boolean).length;

    if (providedIdentityCount !== 1) {
      throw new Error(
        "openEnvironment() requires exactly one of userId, granularId, or a user object returned by recordUser().",
      );
    }

    if (options.user) {
      const user = normalizeUser(options.user);
      return {
        granularId: user.granularId,
        userId: user.userId,
        name: options.name || user.name,
        email: options.email || user.email,
        permissions: options.permissions || user.permissions,
      };
    }

    if (options.userId) {
      const user = await this.recordUser({
        userId: options.userId,
        name: options.name,
        email: options.email,
        permissions: options.permissions,
      });
      return {
        granularId: user.granularId,
        userId: user.userId,
        name: user.name,
        email: user.email,
        permissions: options.permissions || user.permissions,
      };
    }

    if (options.granularId) {
      const subject = await this.subjects.get(options.granularId);
      return {
        granularId: subject.granularId,
        userId: subject.userId,
        name: options.name || subject.name || undefined,
        email: options.email || subject.email || undefined,
        permissions: options.permissions || [],
      };
    }

    throw new Error(
      "openEnvironment() requires either userId, granularId, or a user object returned by recordUser().",
    );
  }

  /**
   * Open or resolve an ontology environment for one user without opening a session.
   *
   * @example
   * ```typescript
   * const environment = await granular.openEnvironment({
   *   ontology: 'my-ontology',
   *   tag: 'dev',
   *   userId: 'user_123',
   *   permissions: ['agent'],
   * });
   *
   * await environment.data.record({
   *   className: 'customer',
   *   id: 'acme',
   *   fields: { name: 'Acme' },
   * });
   *
   * const session = await environment.sessions.create();
   * const job = await session.submitJob(`return "hello";`);
   * console.log(await job.result);
   * ```
   */
  async openEnvironment(options: OpenEnvironmentOptions): Promise<Environment> {
    const resolved = await this.resolveOpenEnvironmentData(
      options,
      "openEnvironment",
    );
    const environment = this.bindEnvironmentHandle(resolved.environment);
    await this.maybeRunEnvironmentImporter(resolved, environment);
    return environment;
  }

  /**
   * Deprecated compatibility alias for `openEnvironment()`.
   *
   * `connect()` no longer opens a runtime session automatically.
   */
  async connect(options: ConnectOptions): Promise<Environment> {
    return this.openEnvironment({
      ...options,
      tag: this.resolveRequestedTag(options, "connect"),
      permissions: options.permissions || options.user?.permissions || [],
    });
  }

  /**
   * Run a registered environment importer against an environment that was
   * opened outside this SDK instance, for example by a delegated browser flow.
   *
   * This uses the same setup-run and queued record-import plumbing as
   * `openEnvironment()`: importer stages, expected object counts, and queued
   * import counters remain visible through `environment.setup` and
   * `getRecordImportSummary()`.
   */
  async runEnvironmentImporterForEnvironment(
    environmentId: string,
    options: RunEnvironmentImporterOptions = {},
  ): Promise<EnvironmentSetupSummary | null> {
    const environmentData = await this.environments.get(environmentId);
    const environment = this.bindEnvironmentHandle(environmentData);
    const requestedOntology =
      options.ontology ||
      environmentData.ontologyId ||
      environmentData.sandboxId;
    return this.runEnvironmentImporter(
      {
        environment: environmentData,
        requestedOntology,
        sandboxId: environmentData.sandboxId,
        subjectId: environmentData.subjectId,
        setupTriggerReason: options.reason || "new_environment",
      },
      environment,
    );
  }

  private resolveRequestedTag(
    options: Pick<ConnectOptions, "tag" | "tagName" | "environment">,
    methodName: string,
  ): string {
    const tag =
      options.tag?.trim() ||
      options.tagName?.trim() ||
      options.environment?.trim();
    if (!tag) {
      throw new Error(`${methodName}() requires \`tag\`.`);
    }
    return tag;
  }

  private buildManagedEnvironmentName(tag: string, versionId: string): string {
    return `__sdk__${tag}__${versionId}__pinned`;
  }

  private isManagedEnvironmentName(
    environment: EnvironmentData,
    tagName: string,
  ): boolean {
    const name = environment.environment || environment.envName || "";
    return name.startsWith(`__sdk__${tagName}__`);
  }

  private isPinnedToVersion(
    environment: EnvironmentData,
    versionId: string,
  ): boolean {
    return (
      environment.buildPolicy.mode === "pinned" &&
      (environment.versionId === versionId ||
        environment.buildPolicy.versionId === versionId ||
        environment.buildPolicy.buildId === versionId)
    );
  }

  private matchesTagTrackedEnvironment(
    environment: EnvironmentData,
    tagName: string,
    tagId: string,
  ): boolean {
    const environmentTagName =
      environment.tag?.name || environment.buildPolicy.tagName || null;
    return (
      environment.tagId === tagId ||
      environmentTagName === tagName ||
      environment.environment === tagName ||
      environment.envName === tagName ||
      environment.environment ===
        this.buildManagedEnvironmentName(tagName, environment.versionId) ||
      environment.envName ===
        this.buildManagedEnvironmentName(tagName, environment.versionId)
    );
  }

  private sortEnvironmentsByRecency(
    environments: EnvironmentData[],
  ): EnvironmentData[] {
    return [...environments].sort(
      (left, right) => right.updatedAt - left.updatedAt,
    );
  }

  private async resolveOpenEnvironmentData(
    options: OpenEnvironmentOptions,
    methodName: string,
  ): Promise<ResolvedOpenEnvironmentData> {
    const ontology = options.ontology;
    if (!ontology) {
      throw new Error(`${methodName}() requires \`ontology\`.`);
    }

    const tagName = options.tag?.trim();
    if (!tagName) {
      throw new Error(`${methodName}() requires \`tag\`.`);
    }
    const user = await this.resolveConnectUser(options);
    if (!Array.isArray(user.permissions) || user.permissions.length === 0) {
      throw new Error(
        `${methodName}() requires at least one permission so the SDK can ensure assignments for new users.`,
      );
    }

    const sandbox = await this.findOrCreateSandbox(ontology);

    for (const profileName of user.permissions) {
      const profileId = await this.ensurePermissionProfile(
        sandbox.sandboxId,
        profileName,
      );
      await this.ensureAssignment(
        user.granularId,
        sandbox.sandboxId,
        profileId,
      );
    }

    const tags = await this.request<{ items: Array<EnvironmentData["tag"]> }>(
      `/control/sandboxes/${sandbox.sandboxId}/tags`,
    );
    const tag = (tags.items || []).find(
      (candidate): candidate is NonNullable<EnvironmentData["tag"]> =>
        Boolean(candidate?.name === tagName),
    );
    if (!tag) {
      throw new Error(
        `Tag "${tagName}" was not found for ontology ${sandbox.sandboxId}.`,
      );
    }

    const targetVersionId = tag.targetVersionId || tag.targetBuildId;
    if (!targetVersionId) {
      throw new Error(
        `Tag "${tagName}" does not currently point to a build/version.`,
      );
    }

    const allEnvironments = await this.environments.list(sandbox.sandboxId);
    const userEnvironments = allEnvironments.filter(
      (environment) => environment.subjectId === user.granularId,
    );

    const currentMatches = this.sortEnvironmentsByRecency(
      userEnvironments.filter(
        (environment) =>
          this.matchesTagTrackedEnvironment(environment, tagName, tag.tagId) &&
          environment.versionId === targetVersionId &&
          (!this.isManagedEnvironmentName(environment, tagName) ||
            this.isPinnedToVersion(environment, targetVersionId)),
      ),
    );
    if (currentMatches.length > 0) {
      return {
        environment: currentMatches[0]!,
        requestedOntology: ontology,
        sandboxId: sandbox.sandboxId,
        subjectId: user.granularId,
      };
    }

    const outdatedMatches = this.sortEnvironmentsByRecency(
      userEnvironments.filter((environment) =>
        this.matchesTagTrackedEnvironment(environment, tagName, tag.tagId),
      ),
    );
    if (outdatedMatches.length > 0 && options.createFreshIfOutdated !== true) {
      return {
        environment: outdatedMatches[0]!,
        requestedOntology: ontology,
        sandboxId: sandbox.sandboxId,
        subjectId: user.granularId,
      };
    }

    return {
      environment: await this.environments.create(sandbox.sandboxId, {
        subjectId: user.granularId,
        environment: this.buildManagedEnvironmentName(tagName, targetVersionId),
        tagId: tag.tagId,
        versionId: targetVersionId,
        permissionProfileId: null,
      }),
      requestedOntology: ontology,
      sandboxId: sandbox.sandboxId,
      subjectId: user.granularId,
      setupTriggerReason:
        outdatedMatches.length > 0
          ? "fresh_after_version_update"
          : "new_environment",
    };
  }

  /**
   * List active (open) sessions for an environment — each session is one agent conversation thread.
   */
  async listOpenSessions(filters: {
    environmentId: string;
  }): Promise<ConversationSessionInfo[]> {
    return this.listSessionsForEnvironment(filters.environmentId, "active");
  }

  /**
   * List closed sessions for an environment (conversations that have disconnected).
   */
  async listClosedSessions(filters: {
    environmentId: string;
  }): Promise<ConversationSessionInfo[]> {
    return this.listSessionsForEnvironment(filters.environmentId, "closed");
  }

  async getUserEnvironmentState(
    options: UserEnvironmentStateOptions & { environmentId: string },
  ): Promise<UserEnvironmentState> {
    const query = new URLSearchParams({
      environmentId: options.environmentId,
    });
    if (options.sessionScope) {
      query.set("sessionScope", options.sessionScope);
    }
    if (options.status) {
      query.set("status", options.status);
    }
    if (typeof options.limit === "number") {
      query.set("limit", String(options.limit));
    }
    if (typeof options.offset === "number") {
      query.set("offset", String(options.offset));
    }
    const state = await this.request<UserEnvironmentState>(
      `/sdk/user-environment-state?${query.toString()}`,
    );
    return this.normalizeUserEnvironmentState(state);
  }

  async markUserEnvironmentSessionsRead(
    options: MarkUserEnvironmentReadOptions & { environmentId: string },
  ): Promise<UserEnvironmentState["readAtBySessionId"]> {
    const result = await this.request<{
      readAtBySessionId?: Record<string, number>;
    }>("/sdk/user-environment-state/read", {
      method: "POST",
      body: JSON.stringify({
        environmentId: options.environmentId,
        sessionId: options.sessionId,
        sessionIds: options.sessionIds,
        readAt: options.readAt,
      }),
    });
    return result.readAtBySessionId || {};
  }

  private async listSessionsForEnvironment(
    environmentId: string,
    status: "active" | "closed",
  ): Promise<ConversationSessionInfo[]> {
    const query = new URLSearchParams({ environmentId, status });
    const res = await this.request<{ items: Record<string, unknown>[] }>(
      `/control/sessions?${query.toString()}`,
    );
    const items = Array.isArray(res.items) ? res.items : [];
    return items.map((row) => this.normalizeConversationSession(row));
  }

  private normalizeConversationSession(
    row: Record<string, unknown>,
  ): ConversationSessionInfo {
    const sessionId = String(row.sessionId ?? row.session_id ?? "");
    const environmentId = String(row.environmentId ?? row.environment_id ?? "");
    return {
      sessionId,
      tenantId: row.tenantId != null ? String(row.tenantId) : undefined,
      environmentId,
      versionId: row.versionId != null ? String(row.versionId) : null,
      docId: String(row.docId ?? row.doc_id ?? ""),
      status: (row.status as ConversationSessionInfo["status"]) || "active",
      createdAt: Granular.coerceIsoDate(row.createdAt ?? row.created_at),
      lastSeenAt: Granular.coerceIsoDate(row.lastSeenAt ?? row.last_seen_at),
      summary: row.summary != null ? String(row.summary) : null,
      summaryUpdatedAt:
        row.summaryUpdatedAt != null || row.summary_updated_at != null
          ? Granular.coerceIsoDate(
              row.summaryUpdatedAt ?? row.summary_updated_at,
            )
          : null,
      subjectId: row.subjectId != null ? String(row.subjectId) : null,
      sessionScope:
        row.sessionScope != null || row.session_scope != null
          ? String(row.sessionScope ?? row.session_scope)
          : null,
      jobCount: typeof row.jobCount === "number" ? row.jobCount : undefined,
      toolCallCount:
        typeof row.toolCallCount === "number" ? row.toolCallCount : undefined,
    };
  }

  private normalizeUserEnvironmentState(
    state: UserEnvironmentState,
  ): UserEnvironmentState {
    return {
      ...state,
      sessions: Array.isArray(state.sessions)
        ? state.sessions.map((item) => ({
            ...item,
            session: this.normalizeConversationSession(
              item.session as unknown as Record<string, unknown>,
            ),
          }))
        : [],
      attention: {
        prompts: Array.isArray(state.attention?.prompts)
          ? state.attention.prompts
          : [],
        count:
          typeof state.attention?.count === "number"
            ? state.attention.count
            : 0,
        activePrompt: state.attention?.activePrompt || null,
      },
      unreadCount:
        typeof state.unreadCount === "number" ? state.unreadCount : 0,
      readAtBySessionId: state.readAtBySessionId || {},
    };
  }

  private static coerceIsoDate(value: unknown): string {
    if (value instanceof Date) {
      return value.toISOString();
    }
    if (typeof value === "number") {
      return new Date(value).toISOString();
    }
    if (typeof value === "string") {
      return value;
    }
    return new Date(0).toISOString();
  }

  /**
   * Create a new session (conversation) for an existing environment and connect to it.
   * The runtime graph is shared across all sessions for the same environment.
   */
  async createSession(options: {
    environmentId: string;
    clientId?: string;
    initialHeap?: CreateSessionOptions["initialHeap"];
  }): Promise<EnvironmentSession> {
    const clientId = options.clientId || `client_${Date.now()}`;
    await this.activateEnvironment(options.environmentId);
    const envData = await this.environments.get(options.environmentId);
    const environment = this.bindEnvironmentHandle(envData);
    const session = await this.request<{
      sessionId: string;
      docId: string;
      wsUrl: string;
      token: string;
      expiresAt: number;
      quota?: GranularQuotaProgress | null;
    }>("/ws/sessions", {
      method: "POST",
      body: JSON.stringify({
        environmentId: options.environmentId,
        clientId,
        initialHeap: options.initialHeap,
      }),
    });
    return this.bindWebSocketEnvironmentSession(environment, clientId, session);
  }

  /**
   * Connect to an existing open session (same conversation thread) using a freshly minted WebSocket token.
   */
  async connectSession(options: {
    sessionId: string;
    clientId?: string;
    maxReconnectAttempts?: number;
    reconnectDelayMs?: number;
  }): Promise<EnvironmentSession> {
    const clientId = options.clientId || `client_${Date.now()}`;
    const minted = await this.request<{
      sessionId: string;
      environmentId: string;
      wsUrl: string;
      token: string;
      expiresAt: number;
    }>(`/ws/sessions/${encodeURIComponent(options.sessionId)}/token`, {
      method: "POST",
      body: JSON.stringify({}),
    });
    const envData = await this.environments.get(minted.environmentId);
    const environment = this.bindEnvironmentHandle(envData);
    return this.bindWebSocketEnvironmentSession(environment, clientId, minted, {
      maxReconnectAttempts: options.maxReconnectAttempts,
      reconnectDelayMs: options.reconnectDelayMs,
    });
  }

  async recordOpenAIUsageSpend(
    usage: OpenAIUsageSpendEvent,
    context?: GranularSpendContext | null,
    options?: { metadata?: Record<string, unknown> | null },
  ): Promise<RecordOpenAIUsageSpendResult> {
    return postOpenAIUsageSpend({
      apiUrl: this.apiUrl,
      token: this.apiKey,
      usage,
      context,
      metadata: options?.metadata,
    });
  }

  /**
   * Mark a session closed in the control plane. If `environment` is the connected handle for that
   * `sessionId`, disconnects the WebSocket so the runtime tears down cleanly.
   */
  async closeSession(
    sessionId: string,
    environment?: EnvironmentSession | null,
  ): Promise<void> {
    if (environment && environment.sessionId === sessionId) {
      await environment.disconnect();
      return;
    }
    await this.request(`/control/sessions/${encodeURIComponent(sessionId)}`, {
      method: "PATCH",
      body: JSON.stringify({
        status: "closed",
        lastSeenAt: new Date().toISOString(),
      }),
    });
  }

  /**
   * Re-open a closed session in the index and connect to its existing runtime document.
   */
  async reopenSession(
    sessionId: string,
    options?: { clientId?: string },
  ): Promise<EnvironmentSession> {
    await this.request(`/control/sessions/${encodeURIComponent(sessionId)}`, {
      method: "PATCH",
      body: JSON.stringify({
        status: "active",
        lastSeenAt: new Date().toISOString(),
      }),
    });
    return this.connectSession({ sessionId, clientId: options?.clientId });
  }

  private resolveEnvironmentImporter(
    requestedOntology: string,
    sandboxId: string,
  ): EnvironmentImporterHandler | undefined {
    const sandboxImporter = this.sandboxImporters.get(sandboxId);
    if (sandboxImporter) {
      const sourceImporter = this.ontologyImporters.get(
        sandboxImporter.sourceOntology,
      );
      if (sourceImporter === sandboxImporter.handler) {
        return sandboxImporter.handler;
      }
      this.sandboxImporters.delete(sandboxId);
    }

    const ontologyImporter = this.ontologyImporters.get(requestedOntology);
    if (!ontologyImporter) {
      return undefined;
    }

    this.sandboxImporters.set(sandboxId, {
      handler: ontologyImporter,
      sourceOntology: requestedOntology,
    });
    return ontologyImporter;
  }

  private async maybeRunEnvironmentImporter(
    resolved: ResolvedOpenEnvironmentData,
    environment: Environment,
  ): Promise<EnvironmentSetupSummary | null> {
    const setupTriggerReason = resolved.setupTriggerReason;
    if (!setupTriggerReason) {
      return null;
    }

    return this.runEnvironmentImporter(
      {
        ...resolved,
        setupTriggerReason,
      },
      environment,
    );
  }

  private async runEnvironmentImporter(
    resolved: ResolvedOpenEnvironmentData & {
      setupTriggerReason: EnvironmentSetupTriggerReason;
    },
    environment: Environment,
  ): Promise<EnvironmentSetupSummary | null> {
    const importer = this.resolveEnvironmentImporter(
      resolved.requestedOntology,
      resolved.sandboxId,
    );
    if (!importer) {
      return null;
    }

    const setupRun = await this.request<EnvironmentSetupSummary>(
      `/control/environments/${environment.environmentId}/setup-runs`,
      {
        method: "POST",
        body: JSON.stringify({
          triggerReason: resolved.setupTriggerReason,
        }),
      },
    );

    const setupRunId = setupRun.setupRunId;
    const updateSetupRun = async (patch: Record<string, unknown>) => {
      await this.request<EnvironmentSetupSummary>(
        `/control/environment-setup-runs/${setupRunId}`,
        {
          method: "PATCH",
          body: JSON.stringify(patch),
        },
      );
    };

    const importerContext: EnvironmentImporter = {
      environmentId: environment.environmentId,
      sandboxId: environment.sandboxId,
      subjectId: environment.subjectId,
      reason: resolved.setupTriggerReason,
      incrementTotalObjectsToImportCount: async (n: number) => {
        const safeIncrement = Math.max(0, Math.trunc(n));
        if (safeIncrement <= 0) {
          return;
        }
        await updateSetupRun({
          incrementTotalObjectsToImportCount: safeIncrement,
        });
      },
      setStage: async (stage: string | null) => {
        await updateSetupRun({ stage });
      },
      importRecords: async (records, options) =>
        environment.enqueueRecordImport(records, {
          batchSize: options?.batchSize,
          writeMode: options?.writeMode,
          setupRunId,
        }),
    };

    try {
      await importer(importerContext);
      const completedSetupRun = await this.request<EnvironmentSetupSummary>(
        `/control/environment-setup-runs/${setupRunId}`,
        {
          method: "PATCH",
          body: JSON.stringify({ markHookCompleted: true }),
        },
      );
      const refreshedEnvironment = await this.environments.get(
        environment.environmentId,
      );
      environment.syncEnvironmentData(refreshedEnvironment);
      return completedSetupRun;
    } catch (error) {
      await updateSetupRun({
        status: "failed",
        errorMessage: error instanceof Error ? error.message : String(error),
      }).catch(() => undefined);
      throw error;
    }
  }

  private bindEnvironmentHandle(envData: EnvironmentData): Environment {
    const graphqlEndpoint = `${this.httpUrl}/orchestrator/graphql`;
    return new Environment(this, envData, this.apiKey, graphqlEndpoint);
  }

  private async bindWebSocketEnvironmentSession(
    environment: Environment,
    clientId: string,
    session: {
      sessionId: string;
      wsUrl: string;
      token: string;
      quota?: GranularQuotaProgress | null;
    },
    transportOptions: {
      maxReconnectAttempts?: number;
      reconnectDelayMs?: number;
    } = {},
  ): Promise<EnvironmentSession> {
    const client = new WSClient({
      url: session.wsUrl,
      sessionId: session.sessionId,
      token: session.token,
      tokenProvider: this.tokenProvider,
      WebSocketCtor: this.WebSocketCtor,
      maxReconnectAttempts: transportOptions.maxReconnectAttempts,
      reconnectDelayMs: transportOptions.reconnectDelayMs,
      onUnexpectedClose: this.onUnexpectedClose,
      onReconnectError: this.onReconnectError,
    });

    await withTimeout(
      client.connect(),
      SESSION_CONNECT_TIMEOUT_MS,
      `session WebSocket connect for ${session.sessionId}`,
    );

    const environmentSession = new EnvironmentSession(
      client,
      environment,
      clientId,
      { initialQuota: session.quota || null },
    );

    await environmentSession.hello();

    return environmentSession;
  }

  private async activateEnvironment(environmentId: string): Promise<void> {
    await this.request<{
      environmentId: string;
      status: "active";
    }>(`/orchestrator/runtime/environments/${environmentId}/activate`, {
      method: "POST",
      body: JSON.stringify({}),
    });
  }

  // ── Sandbox-Level Effects ──

  private getSandboxEffectMap(sandboxId: string): Map<string, ToolWithHandler> {
    let effects = this.sandboxEffects.get(sandboxId);
    if (!effects) {
      effects = new Map();
      this.sandboxEffects.set(sandboxId, effects);
    }
    return effects;
  }

  private serializeEffect(effect: ToolWithHandler): ToolSchema {
    const serialized: ToolSchema = {
      effectKey: computeEffectKey(effect),
      name: effect.name,
      description: effect.description,
      inputSchema: effect.inputSchema,
      stability: effect.stability || "stable",
      provenance: effect.provenance || { source: "custom" },
    };
    if (effect.outputSchema !== undefined) {
      serialized.outputSchema = effect.outputSchema;
    }
    if (effect.tags !== undefined) {
      serialized.tags = effect.tags;
    }
    if (effect.className !== undefined) {
      serialized.className = effect.className;
    }
    if (effect.static !== undefined) {
      serialized.static = effect.static;
    }
    if (effect.versionSelector !== undefined) {
      serialized.versionSelector = effect.versionSelector;
    }
    if (effect.metamodels !== undefined) {
      serialized.metamodels = effect.metamodels;
    }
    return serialized;
  }

  private async publishSandboxEffectCatalog(
    host: SandboxEffectHost,
  ): Promise<void> {
    const effects = Array.from(
      this.getSandboxEffectMap(host.sandboxId).values(),
    ).map((effect) => this.serializeEffect(effect));

    const result = (await withTimeout(
      host.wsClient.call("effects.publishCatalog", {
        effects,
      }),
      EFFECT_CATALOG_SYNC_TIMEOUT_MS,
      `effects.publishCatalog for sandbox ${host.sandboxId}`,
    )) as {
      accepted?: boolean;
      acceptedCount?: number;
      rejected?: Array<{ name?: string; reason?: string }>;
    };

    const acceptedCount =
      typeof result?.acceptedCount === "number" ? result.acceptedCount : 0;
    const rejected = Array.isArray(result?.rejected) ? result.rejected : [];

    if (acceptedCount === 0 && rejected.length > 0) {
      const detail = rejected
        .map(
          (entry) =>
            `${entry.name || "unknown"}: ${entry.reason || "rejected"}`,
        )
        .join("; ");
      throw new Error(
        `Failed to publish live effects for sandbox ${host.sandboxId}: ${detail}`,
      );
    }

    if (rejected.length > 0) {
      console.warn(
        `[Granular] Some live effects were rejected for sandbox ${host.sandboxId}:`,
        rejected,
      );
    }
  }

  private async syncSandboxEffectCatalog(sandboxId: string): Promise<void> {
    let lastError: unknown;
    for (
      let attempt = 1;
      attempt <= EFFECT_CATALOG_SYNC_RETRY_COUNT;
      attempt += 1
    ) {
      try {
        const host = await this.ensureSandboxEffectHost(sandboxId);
        await this.publishSandboxEffectCatalog(host);
        return;
      } catch (error) {
        lastError = error;
        this.disconnectSandboxEffectHost(sandboxId);
        if (
          attempt === EFFECT_CATALOG_SYNC_RETRY_COUNT ||
          !isRetryableEffectRegistrationError(error)
        ) {
          throw error;
        }
        console.warn(
          `[Granular] Retrying effect registration for sandbox ${sandboxId} after transient failure (${attempt}/${EFFECT_CATALOG_SYNC_RETRY_COUNT - 1} retries used):`,
          error,
        );
        await sleep(EFFECT_CATALOG_SYNC_RETRY_DELAY_MS * attempt);
      }
    }

    throw lastError instanceof Error ? lastError : new Error(String(lastError));
  }

  private recoverEffectHost(host: SandboxEffectHost, error: unknown): void {
    if (host.recovering) {
      return;
    }

    if (this.sandboxEffectHosts.get(host.sandboxId) !== host) {
      return;
    }

    host.recovering = true;
    this.stopEffectHostHeartbeat(host);
    this.sandboxEffectHosts.delete(host.sandboxId);

    try {
      host.wsClient.disconnect();
    } catch (disconnectError) {
      console.warn(
        `[Granular] Failed to disconnect stale effect host for sandbox ${host.sandboxId}:`,
        disconnectError,
      );
    }

    void this.ensureSandboxEffectHost(host.sandboxId).catch(
      (reconnectError) => {
        console.error(
          `[Granular] Failed to recover effect host for sandbox ${host.sandboxId} after heartbeat failure:`,
          reconnectError,
        );
        console.error("[Granular] Original heartbeat failure:", error);
      },
    );
  }

  private startEffectHostHeartbeat(host: SandboxEffectHost): void {
    if (host.heartbeatTimer) {
      clearInterval(host.heartbeatTimer);
    }

    host.heartbeatInFlight = false;

    const sendHeartbeat = (
      failureLabel: string,
      recoverOnFailure: boolean,
    ): void => {
      if (host.heartbeatInFlight || host.recovering) {
        return;
      }

      host.heartbeatInFlight = true;
      host.wsClient
        .call("client.heartbeat", {})
        .catch((error) => {
          console.warn(`${failureLabel} ${host.sandboxId}:`, error);
          if (recoverOnFailure) {
            this.recoverEffectHost(host, error);
          }
        })
        .finally(() => {
          host.heartbeatInFlight = false;
        });
    };

    sendHeartbeat(
      "[Granular] Initial effect host heartbeat failed for sandbox",
      false,
    );

    host.heartbeatTimer = setInterval(() => {
      sendHeartbeat(
        "[Granular] Effect host heartbeat failed for sandbox",
        true,
      );
    }, 10_000);
  }

  private stopEffectHostHeartbeat(host?: SandboxEffectHost): void {
    if (!host?.heartbeatTimer) {
      return;
    }
    clearInterval(host.heartbeatTimer);
    host.heartbeatTimer = null;
    host.heartbeatInFlight = false;
  }

  private async synchronizeEffectHost(host: SandboxEffectHost): Promise<void> {
    if (host.syncPromise) {
      return host.syncPromise;
    }

    host.syncPromise = (async () => {
      await host.wsClient.call("client.hello", {
        clientId: host.clientId,
        protocolVersion: "2.0",
      });
      await this.publishSandboxEffectCatalog(host);
      this.startEffectHostHeartbeat(host);
    })();

    try {
      await host.syncPromise;
    } finally {
      if (host.syncPromise) {
        host.syncPromise = null;
      }
    }
  }

  private async ensureSandboxEffectHost(
    sandboxId: string,
  ): Promise<SandboxEffectHost> {
    const existing = this.sandboxEffectHosts.get(sandboxId);
    if (existing) {
      return existing;
    }

    const inflight = this.sandboxEffectHostPromises.get(sandboxId);
    if (inflight) {
      return inflight;
    }

    const connectPromise = (async () => {
      const effectClientId = crypto.randomUUID();
      const clientId = `effect-host:${sandboxId}:${effectClientId}`;
      const wsClient = new WSClient({
        url: buildEffectHostUrl(
          this.apiUrl,
          sandboxId,
          effectClientId,
          clientId,
          this.effectHostUrl,
        ),
        sessionId: `effect-host:${effectClientId}`,
        token: this.apiKey,
        tokenProvider: this.tokenProvider,
        WebSocketCtor: this.WebSocketCtor,
        onUnexpectedClose: this.onUnexpectedClose,
        onReconnectError: this.onReconnectError,
      });

      const host: SandboxEffectHost = {
        sandboxId,
        effectClientId,
        clientId,
        wsClient,
        heartbeatTimer: null,
        heartbeatInFlight: false,
        recovering: false,
        syncPromise: null,
      };

      wsClient.registerRpcHandler("effect.invoke", async (params: unknown) => {
        const request = params as {
          effectKey: string;
          effectName: string;
          input: any;
          context: any;
        };
        return invokeRegisteredEffect(
          this.getSandboxEffectMap(sandboxId),
          request,
        );
      });

      wsClient.on("open", () => {
        void this.synchronizeEffectHost(host).catch((error) => {
          console.error(
            `[Granular] Failed to re-sync effect host for sandbox ${sandboxId}:`,
            error,
          );
        });
      });

      wsClient.on("disconnect", () => {
        this.stopEffectHostHeartbeat(host);
      });

      await withTimeout(
        wsClient.connect(),
        EFFECT_HOST_CONNECT_TIMEOUT_MS,
        `effect host WebSocket connect for sandbox ${sandboxId}`,
      );
      await this.synchronizeEffectHost(host);
      this.sandboxEffectHosts.set(sandboxId, host);
      return host;
    })();

    this.sandboxEffectHostPromises.set(sandboxId, connectPromise);
    try {
      return await connectPromise;
    } finally {
      this.sandboxEffectHostPromises.delete(sandboxId);
    }
  }

  private disconnectSandboxEffectHost(sandboxId: string): void {
    const host = this.sandboxEffectHosts.get(sandboxId);
    if (!host) {
      return;
    }

    this.stopEffectHostHeartbeat(host);
    host.wsClient.disconnect();
    this.sandboxEffectHosts.delete(sandboxId);
  }

  /**
   * Register an effect (tool) for a specific sandbox.
   *
   * @param sandboxNameOrId - The name or ID of the sandbox
   * @param effect - The tool definition and handler
   */
  async registerEffect(
    sandboxNameOrId: string,
    effect: ToolWithHandler,
  ): Promise<void> {
    const sandbox = await this.findOrCreateSandbox(sandboxNameOrId);
    const sandboxId = sandbox.sandboxId;
    this.getSandboxEffectMap(sandboxId).set(
      computeEffectRegistrationKey(effect),
      effect,
    );
    await this.syncSandboxEffectCatalog(sandboxId);
  }

  /**
   * Register multiple effects (tools) for a specific sandbox.
   *
   * batch version of `registerEffect`.
   */
  async registerEffects(
    sandboxNameOrId: string,
    effects: ToolWithHandler[],
  ): Promise<void> {
    const sandbox = await this.findOrCreateSandbox(sandboxNameOrId);
    const sandboxId = sandbox.sandboxId;
    const map = this.getSandboxEffectMap(sandboxId);
    for (const effect of effects) {
      map.set(computeEffectRegistrationKey(effect), effect);
    }
    await this.syncSandboxEffectCatalog(sandboxId);
  }

  /**
   * Unregister an effect from a sandbox.
   *
   * Removes it from the local sandbox registry and updates the
   * sandbox-scoped live catalog.
   */
  async unregisterEffect(sandboxNameOrId: string, name: string): Promise<void> {
    const sandbox = await this.findOrCreateSandbox(sandboxNameOrId);
    const sandboxId = sandbox.sandboxId;
    const currentMap = this.sandboxEffects.get(sandboxId);
    if (!currentMap) {
      return;
    }

    const nextEntries = Array.from(currentMap.entries()).filter(
      ([, effect]) => computeEffectKey(effect) !== name && effect.name !== name,
    );

    if (nextEntries.length === currentMap.size) {
      return;
    }

    const nextMap = new Map(nextEntries);
    this.sandboxEffects.set(sandboxId, nextMap);

    if (nextMap.size === 0) {
      this.disconnectSandboxEffectHost(sandboxId);
      this.sandboxEffects.delete(sandboxId);
      return;
    }

    await this.syncSandboxEffectCatalog(sandboxId);
  }

  /**
   * Disconnect one sandbox-scoped effect host, or all of them when no sandbox is provided.
   *
   * This is primarily useful for long-lived helper processes such as generated
   * `granular-effects.ts` scripts that need to shut down cleanly on SIGINT/SIGTERM.
   */
  async disconnectEffects(sandboxNameOrId?: string): Promise<void> {
    if (sandboxNameOrId) {
      const sandbox = await this.findOrCreateSandbox(sandboxNameOrId);
      this.disconnectSandboxEffectHost(sandbox.sandboxId);
      return;
    }

    for (const sandboxId of Array.from(this.sandboxEffectHosts.keys())) {
      this.disconnectSandboxEffectHost(sandboxId);
    }
  }

  /**
   * Unregister all effects for a sandbox.
   */
  async unregisterAllEffects(sandboxNameOrId: string): Promise<void> {
    const sandbox = await this.findOrCreateSandbox(sandboxNameOrId);
    const sandboxId = sandbox.sandboxId;
    this.sandboxEffects.delete(sandboxId);
    this.disconnectSandboxEffectHost(sandboxId);
  }

  /**
   * Find a sandbox by name or create it if it doesn't exist
   */
  private async findOrCreateSandbox(nameOrId: string): Promise<Sandbox> {
    try {
      // Try to get by ID first
      const sandbox = await this.sandboxes.get(nameOrId);
      return sandbox;
    } catch (error) {
      if (nameOrId.startsWith("sbx_")) {
        throw error;
      }
      // If not found by ID, try to find by name or create
      const sandboxes = await this.sandboxes.list();
      const existing = sandboxes.items.find((s) => s.name === nameOrId);
      if (existing) {
        return existing;
      }

      // Create new sandbox
      const created = await this.sandboxes.create({ name: nameOrId });
      return created;
    }
  }

  /**
   * Ensure a permission profile exists for a sandbox, creating it if needed.
   * If profileName matches an existing profile name, returns its ID.
   * Otherwise, creates a v1 source-profile file shape with an allow default.
   */
  private async ensurePermissionProfile(
    sandboxId: string,
    profileName: string,
  ): Promise<string> {
    try {
      // Check if profile already exists by name
      const profiles = await this.permissionProfiles.list(sandboxId);
      const existing = profiles.find((p) => p.name === profileName);
      if (existing) {
        return existing.permissionProfileId;
      }
    } catch {
      // Ignore lookup errors, try to create
    }

    // Create a source-profile shape so future builds compile it through the
    // versioned policy pipeline instead of relying on legacy effect allow/deny.
    const created = await this.permissionProfiles.create(sandboxId, {
      name: profileName,
      rules: {
        schemaVersion: 1,
        name: profileName,
        description:
          profileName === "allow-all"
            ? "Every declared action is visible unless a manifest policy denies it."
            : `Generated permission profile ${profileName}`,
        defaults: { actionPolicy: "allow" },
        actions: [],
      },
    });
    return created.permissionProfileId;
  }

  /**
   * Ensure an assignment exists for a subject in a sandbox with a permission profile
   */
  private async ensureAssignment(
    subjectId: string,
    sandboxId: string,
    permissionProfileId: string,
  ): Promise<void> {
    try {
      const assignments = await this.request<AssignmentListResponse>(
        `/control/subjects/${subjectId}/assignments`,
      );
      // Check for ANY assignment in this sandbox
      const existing = assignments.items.find((a) => a.sandboxId === sandboxId);

      if (existing) {
        // If assignment exists with same profile, we are done
        if (existing.permissionProfileId === permissionProfileId) {
          return;
        }

        // If assignment exists but with different profile, delete it first
        // This avoids UNIQUE constraint violation when creating the new one
        await this.request<DeleteResponse>(
          `/control/assignments/${existing.assignmentId}`,
          {
            method: "DELETE",
          },
        );
      }
    } catch {
      // Ignore lookup errors
    }

    await this.request<Assignment>(
      `/control/subjects/${subjectId}/assignments`,
      {
        method: "POST",
        body: JSON.stringify({
          sandboxId,
          subjectId,
          permissionProfileId,
        }),
      },
    );
  }

  /**
   * Sandbox management API
   */
  get sandboxes() {
    return {
      list: async (): Promise<SandboxListResponse> => {
        return this.request<SandboxListResponse>("/control/sandboxes");
      },
      get: async (id: string): Promise<Sandbox> => {
        return this.request<Sandbox>(`/control/sandboxes/${id}`);
      },
      create: async (data: CreateSandboxData): Promise<Sandbox> => {
        return this.request<Sandbox>("/control/sandboxes", {
          method: "POST",
          body: JSON.stringify(data),
        });
      },
      update: async (
        id: string,
        data: Partial<CreateSandboxData>,
      ): Promise<Sandbox> => {
        return this.request<Sandbox>(`/control/sandboxes/${id}`, {
          method: "PATCH",
          body: JSON.stringify(data),
        });
      },
      delete: async (id: string): Promise<DeleteResponse> => {
        return this.request<DeleteResponse>(`/control/sandboxes/${id}`, {
          method: "DELETE",
        });
      },
    };
  }

  /**
   * Permission Profile management for sandboxes
   */
  get permissionProfiles() {
    const profileSourceFromRecord = (
      record: PermissionProfile,
    ): PermissionProfileFile => {
      const profile =
        record.profile || record.rules || ({} as PermissionProfileFile);
      return {
        ...profile,
        schemaVersion: profile.schemaVersion || 1,
        name: profile.name || record.name,
        description: profile.description || record.description,
      };
    };

    return {
      list: async (sandboxId: string): Promise<PermissionProfile[]> => {
        const result = await this.request<PermissionProfileListResponse>(
          `/control/sandboxes/${sandboxId}/permission-profile-sources`,
        );
        return result.items;
      },
      get: async (
        sandboxId: string,
        profileId: string,
      ): Promise<PermissionProfile> => {
        const result = await this.request<PermissionProfileListResponse>(
          `/control/sandboxes/${sandboxId}/permission-profile-sources`,
        );
        const profile = result.items.find(
          (item) =>
            item.permissionProfileId === profileId || item.name === profileId,
        );
        if (!profile) {
          throw new Error(`Permission profile source not found: ${profileId}`);
        }
        return profile;
      },
      create: async (
        sandboxId: string,
        data: CreatePermissionProfileData,
      ): Promise<PermissionProfile> => {
        const profile = {
          ...(data.rules as unknown as Record<string, unknown>),
          schemaVersion: 1,
          name: data.name,
        };
        const existingProfiles = await this.permissionProfiles.list(sandboxId);
        const profiles = [
          ...existingProfiles
            .filter((existing) => existing.name !== data.name)
            .map((existing) => profileSourceFromRecord(existing)),
          profile,
        ];
        const result = await this.request<PermissionProfileListResponse>(
          `/control/sandboxes/${sandboxId}/permission-profile-sources`,
          {
            method: "PUT",
            body: JSON.stringify({ profiles }),
          },
        );
        const synced =
          result.items.find((item) => item.name === data.name) ||
          result.items[0];
        if (!synced) {
          throw new Error(
            `Permission profile source sync did not return ${data.name}`,
          );
        }
        return synced;
      },
      delete: async (
        _sandboxId: string,
        _profileId: string,
      ): Promise<DeleteResponse> => {
        throw new Error(
          "Permission profile sources are updated by syncing the desired source set.",
        );
      },
    };
  }

  /**
   * Environment management
   */
  get environments() {
    return {
      list: async (sandboxId: string): Promise<EnvironmentData[]> => {
        const result = await this.request<EnvironmentListResponse>(
          `/control/sandboxes/${sandboxId}/environments`,
        );
        return result.items.map(normalizeEnvironmentData);
      },
      get: async (environmentId: string): Promise<EnvironmentData> => {
        return normalizeEnvironmentData(
          await this.request<EnvironmentData>(
            `/control/environments/${environmentId}`,
          ),
        );
      },
      create: async (
        sandboxId: string,
        data: CreateEnvironmentData,
      ): Promise<EnvironmentData> => {
        const environmentName = data.environment || data.envName;
        return normalizeEnvironmentData(
          await this.request<EnvironmentData>(
            `/control/sandboxes/${sandboxId}/environments`,
            {
              method: "POST",
              body: JSON.stringify({
                ...data,
                envName: environmentName,
              }),
            },
          ),
        );
      },
      delete: async (environmentId: string): Promise<DeleteResponse> => {
        return this.request<DeleteResponse>(
          `/control/environments/${environmentId}`,
          {
            method: "DELETE",
          },
        );
      },
    };
  }

  /**
   * Event stream operations: query, subscribe, and acknowledge stream events
   */
  get streams() {
    return {
      getEvents: async (params: {
        ontology: string;
        stream: string;
        environment?: string;
        session?: string;
        eventTypes?: string[];
        since?: Date;
        until?: Date;
        isAcked?: boolean;
        limit?: number;
        offset?: number;
      }): Promise<StreamEvent[]> => {
        const sandbox = await this._resolveSandboxId(params.ontology);
        const query = new URLSearchParams({ sandboxId: sandbox });
        if (params.environment) query.set("environmentId", params.environment);
        if (params.session) query.set("sessionId", params.session);
        if (params.stream) query.set("streamName", params.stream);
        if (params.eventTypes && params.eventTypes.length > 0) {
          query.set("eventTypes", params.eventTypes.join(","));
        }
        if (params.since) query.set("since", params.since.toISOString());
        if (params.until) query.set("until", params.until.toISOString());
        if (params.isAcked !== undefined)
          query.set("isAcked", params.isAcked ? "1" : "0");
        if (params.limit) query.set("limit", String(params.limit));
        if (params.offset) query.set("offset", String(params.offset));

        const result = await this.request<{ items: any[] }>(
          `/control/stream-events?${query.toString()}`,
        );
        return (result.items || []).map((row: any) => ({
          eventId: row.event_id,
          streamName: row.stream_name,
          eventType: row.event_type,
          payload:
            typeof row.payload === "string"
              ? JSON.parse(row.payload)
              : row.payload,
          environmentId: row.environment_id,
          sessionId: row.session_id,
          subjectId: row.subject_id,
          source: row.source,
          isAcked: Boolean(row.is_acked),
          createdAt: row.created_at,
        }));
      },

      subscribe: (params: {
        ontology: string;
        stream: string;
        environment?: string;
        session?: string;
        eventTypes?: string[];
        since?: Date;
        onEvent: (event: StreamEvent) => void;
        onError?: (err: Error) => void;
        pollIntervalMs?: number;
      }): StreamSubscription => {
        const interval = params.pollIntervalMs ?? 5000;
        let cursor = params.since || new Date();
        let running = true;
        const seenEventIds = new Set<string>();

        const poll = async () => {
          while (running) {
            try {
              const events = await this.streams.getEvents({
                ontology: params.ontology,
                stream: params.stream,
                environment: params.environment,
                session: params.session,
                eventTypes: params.eventTypes,
                since: cursor,
                limit: 100,
              });

              const orderedEvents = [...events].sort(
                (a, b) => a.createdAt - b.createdAt,
              );
              for (const event of orderedEvents) {
                if (seenEventIds.has(event.eventId)) {
                  continue;
                }
                seenEventIds.add(event.eventId);
                const eventTime = new Date(event.createdAt * 1000);
                if (eventTime > cursor) {
                  cursor = eventTime;
                }
                params.onEvent(event);
              }
            } catch (err) {
              params.onError?.(
                err instanceof Error ? err : new Error(String(err)),
              );
            }

            await new Promise((resolve) => setTimeout(resolve, interval));
          }
        };

        poll();
        return {
          unsubscribe: () => {
            running = false;
          },
        };
      },

      ack: async (eventId: string): Promise<void> => {
        await this.request("/control/stream-events/ack", {
          method: "POST",
          body: JSON.stringify({ eventIds: [eventId] }),
        });
      },

      ackBatch: async (eventIds: string[]): Promise<void> => {
        await this.request("/control/stream-events/ack", {
          method: "POST",
          body: JSON.stringify({ eventIds }),
        });
      },

      getStats: async (params: {
        ontology: string;
        environment?: string;
      }): Promise<StreamStats[]> => {
        const sandbox = await this._resolveSandboxId(params.ontology);
        const query = new URLSearchParams({ sandboxId: sandbox });
        if (params.environment) query.set("environmentId", params.environment);

        const result = await this.request<{ items: any[] }>(
          `/control/stream-events/stats?${query.toString()}`,
        );
        return (result.items || []).map((row: any) => ({
          streamName: row.stream_name,
          eventType: row.event_type,
          total: Number(row.total),
          last1h: Number(row.last_1h),
          last24h: Number(row.last_24h),
          unacked: Number(row.unacked),
        }));
      },
    };
  }

  /**
   * Subject management
   */
  get subjects() {
    return {
      get: async (subjectId: string): Promise<Subject> => {
        return normalizeSubject(
          await this.request<Subject>(`/control/subjects/${subjectId}`),
        );
      },
      listAssignments: async (
        subjectId: string,
      ): Promise<AssignmentListResponse> => {
        return this.request<AssignmentListResponse>(
          `/control/subjects/${subjectId}/assignments`,
        );
      },
    };
  }

  /**
   * @deprecated Use recordUser() instead
   */
  get users() {
    return {
      create: async (data: {
        id: string;
        name?: string;
        email?: string;
      }): Promise<Subject> => {
        return normalizeSubject(
          await this.request<Subject>("/control/subjects", {
            method: "POST",
            body: JSON.stringify({
              identityId: data.id,
              name: data.name,
              email: data.email,
            }),
          }),
        );
      },
      get: async (id: string): Promise<Subject> => {
        return normalizeSubject(
          await this.request<Subject>(`/control/subjects/${id}`),
        );
      },
    };
  }

  private async _resolveSandboxId(ontologyNameOrId: string): Promise<string> {
    if (ontologyNameOrId.startsWith("sbx_")) return ontologyNameOrId;
    const result = await this.request<SandboxListResponse>(
      `/control/sandboxes?name=${encodeURIComponent(ontologyNameOrId)}`,
    );
    if (result.items.length === 0)
      throw new Error(`Ontology not found: ${ontologyNameOrId}`);
    return result.items[0]!.sandboxId;
  }

  /**
   * Make an authenticated API request
   */
  private async request<T>(
    path: string,
    options: RequestInit = {},
  ): Promise<T> {
    const url = `${this.httpUrl}${path}`;
    if (this.debugHttp) {
      console.log(`[SDK] Requesting: ${url}`);
    }

    for (
      let attempt = 1;
      attempt <= LOCAL_CONTROL_REQUEST_RETRY_COUNT;
      attempt += 1
    ) {
      const response = await fetch(url, {
        ...options,
        headers: {
          Authorization: `Bearer ${this.apiKey}`,
          "Content-Type": "application/json",
          Connection: "close",
          ...options.headers,
        },
      });

      if (response.ok) {
        if (response.status === 204) {
          return { deleted: true } as T;
        }
        return response.json() as Promise<T>;
      }

      const errorText = await response.text();
      const retryable = isRetryableLocalWorkerRestart(
        response.status,
        errorText,
        url,
      );
      if (retryable && attempt < LOCAL_CONTROL_REQUEST_RETRY_COUNT) {
        if (this.debugHttp) {
          console.warn(
            `[SDK] Retrying local control request after worker restart (${attempt}/${LOCAL_CONTROL_REQUEST_RETRY_COUNT - 1} retries used): ${url}`,
          );
        }
        await sleep(LOCAL_CONTROL_REQUEST_RETRY_DELAY_MS * attempt);
        continue;
      }

      throw new Error(`Granular API Error (${response.status}): ${errorText}`);
    }

    throw new Error(`Granular API Error: exhausted retries for ${url}`);
  }
}
